import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/css';
import SettingsCard from '../../../../../shared/components/structure/SettingsCard';
import ContentCollapsingSection from '../../../../../shared/components/structure/ContentCollapsingSection';
import CheckboxField from '../../../../../shared/components/forms/CheckboxField';
import * as voicifyApi from '../../../../../shared/api';
import OrganizationVariablesContainer from '../../../../../shared/hooks/OrganizationVariablesContainer';
import OrganizationVariablesList from '../../../../../shared/scenes/organizationSettings/components/OrganizationVariablesList';
import SonioxContextEditor from '../../../../../shared/scenes/applicationSettings/components/telephonyDeployments/SonioxContextEditor';
import Button from '../../../../../shared/components/general/Button';
import TooltipWrapper from '../../../../../shared/components/general/TooltipWrapper';
import IconTooltip from '../../../../../shared/components/general/IconTooltip';
const infoIcon = require('../../../../../shared/content/images/information-circle.svg');
import PageError from '../../../../../shared/components/general/PageError';
import Loader from '../../../../../shared/components/general/Loader';
import useBulkAppOperations from '../../../../../shared/hooks/useBulkAppOperations';
import ApplicationModel from '../../../../../shared/models/applications/api/ApplicationModel';
import ApplicationContainer from '../../../../../shared/state/containers/ApplicationContainer';
import AppliedApplicationTemplateContainer from '../../../../../shared/state/containers/AppliedApplicationTemplateContainer';
import ApplicationEnvironmentsContainer from '../../../../../shared/state/containers/ApplicationEnvironmentsContainer';
import { dark_grey_blue, silver_two, color_shades_dark, color_colors_light_blue, color_shades_darker } from '../../../../../shared/constants/colors';
import { getBaseUrl, getSparkMainBaseUrl } from '../../../../../shared/constants/Urls';
import { TelephonyDeploymentResponse } from '../../../../../shared/models/telephonyDeployment/TelephonyDeploymentResponse';
import { getSonioxContextValidationError } from '../../../../../shared/scenes/applicationSettings/components/telephonyDeployments/sonioxContextUtils';
import { FoodOrderingFeatureFlagId, GenAIFoodOrdering, HealthcareSchedulingFeatureFlagId } from '../../../../../shared/constants/featureFlagIds';
import { shouldValidatePosConfig, SparkApplicationType } from './sparkPosConfigUtils';
import { FoodOrderingGenAIModelSettingId, FoodOrderingGenAIPromptOverrideSettingId } from '../../../../../shared/constants/featureFlagSettingIds';
import { SPEEDLINE } from '../../../../../shared/constants/posNames';
import { voiceOptions } from '../../../../../shared/constants/sparkConstants/voiceOptions';
import { GenAIModels } from '../../../../../shared/constants/genAiModels';
import { formatHoursToday, getEarliestStartMinutes } from '../../../../../shared/utils/weeklyScheduleFormatters';
import { formatTransferNumber } from '../../../../../shared/utils/phoneFormatters';
import { updateApplicationModuleField } from '../../../../onboarding/api/applicationModuleFields';
import {
    buildTelephonyUpdateRequest,
    buildBulkVoiceUpdateRequest,
    buildBulkLiveUpdateRequest,
    buildFeatureFlagUpdateRequest,
    buildClosedDaysUpdateRequest,
    resolveExistingClosedDays,
    resolveGenAIProviderVar
} from './SparkManagementUpdateUtils';

const getVoiceFriendlyName = (voiceString: string): string => {
    const match = voiceOptions.find(opt => opt.voice === voiceString);
    if (match) return match.value;

    if (voiceString?.includes('voice_id=')) {
        const idMatch = voiceString.match(/voice_id=([^|]+)/);
        if (idMatch) return `Custom (${idMatch[1].substring(0, 6)}...)`;
    }
    return 'Custom';
};

const getFeatureFlagSettingValue = (app: ApplicationModel, settingId: string): string | null => {
    if (!app.featureFlags) return null;
    for (const flag of app.featureFlags) {
        if (flag.featureFlagSettings) {
            const setting = flag.featureFlagSettings.find(s => s.id === settingId);
            if (setting) return setting.value || null;
        }
    }
    return null;
};

const nthWeekdayOfMonth = (year: number, monthIndex: number, weekday: number, occurrence: number): string => {
    const date = new Date(Date.UTC(year, monthIndex, 1));
    const offset = (weekday - date.getUTCDay() + 7) % 7;
    date.setUTCDate(1 + offset + ((occurrence - 1) * 7));
    return date.toISOString().slice(0, 10);
};

const lastWeekdayOfMonth = (year: number, monthIndex: number, weekday: number): string => {
    const date = new Date(Date.UTC(year, monthIndex + 1, 0));
    const offset = (date.getUTCDay() - weekday + 7) % 7;
    date.setUTCDate(date.getUTCDate() - offset);
    return date.toISOString().slice(0, 10);
};

const getHolidaysMapForYear = (year: number): Record<string, string> => {
    const map: Record<string, string> = {};
    const add = (label: string, dateStr: string) => {
        map[dateStr] = label;
    };
    add("New Year's Day", `${year}-01-01`);
    add("Martin Luther King Jr. Day", nthWeekdayOfMonth(year, 0, 1, 3));
    add("Presidents Day", nthWeekdayOfMonth(year, 1, 1, 3));
    add("Memorial Day", lastWeekdayOfMonth(year, 4, 1));
    add("Juneteenth", `${year}-06-19`);
    add("Independence Day", `${year}-07-04`);
    add("Labor Day", nthWeekdayOfMonth(year, 8, 1, 1));
    add("Columbus Day", nthWeekdayOfMonth(year, 9, 1, 2));
    add("Veterans Day", `${year}-11-11`);
    add("Thanksgiving Day", nthWeekdayOfMonth(year, 10, 4, 4));
    add("Christmas Eve", `${year}-12-24`);
    add("Christmas Day", `${year}-12-25`);
    return map;
};

const getClosedDaysThisYear = (disabledDays: string): string[] => {
    if (!disabledDays || disabledDays.trim().toLowerCase() === 'none') {
        return [];
    }

    const currentYear = new Date().getFullYear();
    const holidaysMap = getHolidaysMapForYear(currentYear);
    const result: string[] = [];

    const entries = disabledDays.split(',');
    for (const entry of entries) {
        const trimmed = entry.trim();
        if (!trimmed) continue;

        const datePart = trimmed.includes('(') ? trimmed.split('(')[0].trim() : trimmed;
        const parts = datePart.split('/');

        let month = 0;
        let day = 0;
        let year = currentYear;

        if (parts.length === 3) {
            month = parseInt(parts[0], 10);
            day = parseInt(parts[1], 10);
            year = parseInt(parts[2], 10);
            if (year < 100) {
                year += year < 50 ? 2000 : 1900;
            }
        } else if (parts.length === 2) {
            month = parseInt(parts[0], 10);
            day = parseInt(parts[1], 10);
        } else {
            continue;
        }

        if (isNaN(month) || isNaN(day) || isNaN(year)) {
            continue;
        }

        if (year === currentYear) {
            const isoDate = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
            const holidayName = holidaysMap[isoDate];
            if (holidayName) {
                result.push(holidayName);
            } else {
                const displayDate = `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`;
                result.push(displayDate);
            }
        }
    }

    return Array.from(new Set(result));
};

const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return 'Never';
    try {
        const date = new Date(dateStr);
        return date.toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch {
        return 'Invalid Date';
    }
};

const formatDateShort = (dateStr: string | null | undefined): string => {
    if (!dateStr) return 'Never';
    try {
        const date = new Date(dateStr);
        return date.toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric'
        });
    } catch {
        return 'Invalid Date';
    }
};

const formatDateOnly = (dateStr: string | null | undefined): string => {
    if (!dateStr) return 'Never';
    try {
        const date = new Date(dateStr);
        return date.toLocaleDateString(undefined, {
            timeZone: 'UTC',
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    } catch {
        return 'Invalid Date';
    }
};

const getPosConfigIssues = (
    posConfig: string | null | undefined,
    applicationId?: string,
    applicationSecret?: string
): string[] => {
    if (!posConfig || !posConfig.trim()) {
        return ['POS config is missing.'];
    }

    try {
        const parsedConfig = JSON.parse(posConfig);
        if (!parsedConfig || Array.isArray(parsedConfig) || typeof parsedConfig !== 'object') {
            return ['POS config must be a JSON object.'];
        }

        const issues: string[] = [];

        if (typeof parsedConfig.applicationId !== 'string' || !parsedConfig.applicationId.trim()) {
            issues.push('POS config is missing applicationId.');
        } else if (applicationId && parsedConfig.applicationId !== applicationId) {
            issues.push('POS config applicationId does not match this application.');
        }

        if (typeof parsedConfig.applicationSecret !== 'string' || !parsedConfig.applicationSecret.trim()) {
            issues.push('POS config is missing applicationSecret.');
        } else if (applicationSecret && parsedConfig.applicationSecret !== applicationSecret) {
            issues.push('POS config applicationSecret does not match this application.');
        }

        return issues;
    } catch {
        return ['POS config contains invalid JSON.'];
    }
};

interface RowWarning {
    type: 'sync' | 'publish' | 'voice' | 'asr' | 'proactive-outbound' | 'proactive-inbound' | 'no-proactive-inbound' | 'spanish-mismatch' | 'launch-missed' | 'dummy-transfer' | 'no-transfer' | 'no-closed-days' | 'genai-prompt-mismatch' | 'pos-config';
    message: string;
    level: 'p1' | 'p2';
}

const getResolvedGenAIModel = (app: ApplicationModel, status: LocationStatus, sharedStatus?: LocationStatus): string => {
    const type = getAppType(app);
    if (type === 'Healthcare') {
        return status.genAiModelRaw || (sharedStatus ? sharedStatus.genAiModelRaw || '' : '');
    }
    return getFeatureFlagSettingValue(app, FoodOrderingGenAIModelSettingId) || '';
};

const getAppType = (app: ApplicationModel): SparkApplicationType => {
    if (!app.featureFlags) return 'AI Answering';
    const hasOrdering = app.featureFlags.some(ff => ff.id === FoodOrderingFeatureFlagId || ff.id === GenAIFoodOrdering);
    if (hasOrdering) return 'Ordering';
    const hasHealthcare = app.featureFlags.some(ff => ff.id === HealthcareSchedulingFeatureFlagId);
    if (hasHealthcare) return 'Healthcare';
    return 'AI Answering';
};

const extractVoiceId = (voiceString: string | null | undefined): string => {
    if (!voiceString) return '';
    const trimmed = voiceString.trim();
    if (trimmed.includes('voice_id=')) {
        const match = trimmed.match(/voice_id=([^|]+)/);
        if (match && match[1]) {
            return match[1].trim();
        }
    }
    return trimmed;
};

const deriveRowWarnings = (
    app: ApplicationModel,
    status: LocationStatus,
    sharedStatus?: LocationStatus,
    organizationHasDentalApplication: boolean = false
): RowWarning[] => {
    if (status.isLoading) return [];
    const list: RowWarning[] = [];
    const appType = getAppType(app);
    const isDental = appType === 'Healthcare';

    if (shouldValidatePosConfig(app.allowsTemplating, appType, organizationHasDentalApplication)) {
        (status.posConfigIssues || []).forEach(message => {
            list.push({
                type: 'pos-config',
                message,
                level: 'p1'
            });
        });
    }

    // 1. Sync warning: > 7 days ago or never synced
    if (status.hasModules) {
        if (!status.lastSync) {
            list.push({
                type: 'sync',
                message: 'This location has never been synced.',
                level: 'p1'
            });
        } else {
            const syncDate = new Date(status.lastSync);
            const sevenDaysAgo = new Date();
            sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
            if (syncDate < sevenDaysAgo) {
                list.push({
                    type: 'sync',
                    message: 'Last sync was more than 7 days ago.',
                    level: 'p2'
                });
            }
        }
    }

    // 2. Publish warning: > 7 days ago or never published (applicable to all applications including shared configuration)
    if (!status.lastPublish) {
        list.push({
            type: 'publish',
            message: 'This location has never been published.',
            level: 'p1'
        });
    } else {
        const pubDate = new Date(status.lastPublish);
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        if (pubDate < sevenDaysAgo) {
            list.push({
                type: 'publish',
                message: 'Last publish was more than 7 days ago.',
                level: 'p2'
            });
        }
    }

    if (!app.allowsTemplating) {
        // Transfer number checking
        if (status.transferTarget) {
            const cleaned = status.transferTarget.replace(/\D/g, '');
            const isDummy = cleaned === '10000000000' || new RegExp('^1(.)\\\\1{9}$').test(cleaned) || new RegExp('^(.)\\\\1{9}$').test(cleaned);
            if (isDummy) {
                list.push({
                    type: 'dummy-transfer',
                    message: 'Dummy transfer number detected.',
                    level: 'p2'
                });
            }
        } else {
            if (app.isLive) {
                list.push({
                    type: 'no-transfer',
                    message: 'No transfer number configured for a live app.',
                    level: 'p1'
                });
            } else {
                list.push({
                    type: 'no-transfer',
                    message: 'No transfer number configured.',
                    level: 'p2'
                });
            }
        }

        if (status.rawDeployments && status.rawDeployments.length > 0) {
            // 3. Voice warning: mismatch (comparing extracted voice ID only)
            const voices = status.rawDeployments
                .map(d => extractVoiceId(d.ttsOptions?.[0]?.ttsVoice || d.ttsVoice || ''))
                .filter(Boolean);
            const uniqueVoices = Array.from(new Set(voices));
            if (uniqueVoices.length > 1 && isDental) {
                list.push({
                    type: 'voice',
                    message: 'This app uses different voices across inbound and outbound. Consider aligning for consistency.',
                    level: 'p2'
                });
            }

            // 4. Outbound proactive warning: outbound must not use proactive response (only if actively configured/has text)
            const hasOutboundProactive = status.rawDeployments.some(d => {
                const isOutbound = d.useForOutboundCalling === true;
                if (isOutbound) {
                    const hasProactiveStandard = d.useProactiveResponses === true && d.proactiveResponses && d.proactiveResponses.trim() !== '' && d.proactiveResponses.trim().toLowerCase() !== 'none';
                    const hasProactiveGen = d.useGenerativeProactiveResponse === true && d.generativeProactiveResponsePrompt && d.generativeProactiveResponsePrompt.trim() !== '' && d.generativeProactiveResponsePrompt.trim().toLowerCase() !== 'none';
                    return hasProactiveStandard || hasProactiveGen;
                }
                return false;
            });
            if (hasOutboundProactive && isDental) {
                list.push({
                    type: 'proactive-outbound',
                    message: 'Outbound lines should not use proactive responses.',
                    level: 'p2'
                });
            }

            // 5. Inbound proactive warning: mismatch with standard constant
            status.rawDeployments.forEach(d => {
                const isOutbound = d.useForOutboundCalling === true;
                if (!isOutbound) {
                    const isSpanish = (d.locale || '').toLowerCase().startsWith('es');
                    const expected = isDental
                        ? (isSpanish ? '{{constant.generative_proactive_response_dental_spanish}}' : '{{constant.generative_proactive_response_dental}}')
                        : (isSpanish ? '{{constant.generative_proactive_response_ordering_spanish}}' : '{{constant.proactive_response_prompt}}');

                    const hasProactive = d.useGenerativeProactiveResponse || d.useProactiveResponses;
                    const currentPrompt = d.useGenerativeProactiveResponse
                        ? d.generativeProactiveResponsePrompt
                        : d.proactiveResponses;

                    const hasValidPrompt = currentPrompt && currentPrompt.trim() !== '' && currentPrompt.trim().toLowerCase() !== 'none';

                    if (!hasProactive || !hasValidPrompt) {
                        list.push({
                            type: 'no-proactive-inbound',
                            message: `No proactive response value configured on inbound telephony line ${d.phoneNumber || ''}.`,
                            level: 'p1'
                        });
                    } else if (currentPrompt?.trim() !== expected) {
                        list.push({
                            type: 'proactive-inbound',
                            message: `Inbound proactive response for ${d.phoneNumber || 'line'} should use the standard constant.`,
                            level: 'p2'
                        });
                    }
                }
            });

            // 6. Spanish mismatch warning: enabled on inbound but not outbound (only if outbound is present)
            const hasEsInbound = status.rawDeployments?.some(d => !d.useForOutboundCalling && (d.locale || '').toLowerCase().startsWith('es')) || false;
            const hasEsOutbound = status.rawDeployments?.some(d => d.useForOutboundCalling && (d.locale || '').toLowerCase().startsWith('es')) || false;
            const hasOutbound = status.rawDeployments?.some(d => d.useForOutboundCalling) || false;
            if (hasEsInbound && !hasEsOutbound && isDental && hasOutbound) {
                list.push({
                    type: 'spanish-mismatch',
                    message: 'Spanish is enabled for inbound but not outbound.',
                    level: 'p1'
                });
            }

            // 7. ASR mismatch warning: different ASR providers/hints across lines
            const asrs = status.rawDeployments
                .map(d => d.asrProvider || 'Deepgram');
            const uniqueAsrs = Array.from(new Set(asrs));
            if (uniqueAsrs.length > 1) {
                list.push({
                    type: 'asr',
                    message: 'This app uses different ASR providers across its lines.',
                    level: 'p2'
                });
            }
        }
    }

    // 7. Missed launch date warning: draft but launch date in past, OR no target launch date
    if (!app.isLive) {
        if (app.targetLaunchDate) {
            const launchDate = new Date(app.targetLaunchDate);
            const now = new Date();
            if (launchDate < now) {
                list.push({
                    type: 'launch-missed',
                    message: `Target launch date (${launchDate.toLocaleDateString()}) is in the past, but location is still in draft status.`,
                    level: 'p2'
                });
            }
        } else {
            list.push({
                type: 'launch-missed',
                message: 'No target launch date set for this draft location.',
                level: 'p2'
            });
        }
    }

    // 8. No closed days warning
    const closedDays = getClosedDaysThisYear(status.disabledDaysRaw || (sharedStatus ? sharedStatus.disabledDaysRaw || '' : ''));
    if (closedDays.length === 0) {
        list.push({
            type: 'no-closed-days',
            message: 'Configure closed days for this location.',
            level: 'p1'
        });
    }

    // 9. GenAI Prompt Mismatch warning: P1 if model is gemini-3.5-flash and prompt is not standard new prompt
    if (appType === 'Ordering') {
        const genAIModel = getFeatureFlagSettingValue(app, FoodOrderingGenAIModelSettingId);
        const genAIPrompt = getFeatureFlagSettingValue(app, FoodOrderingGenAIPromptOverrideSettingId);
        if (genAIModel === 'gemini-3.5-flash' && genAIPrompt !== '{{constant.new_ordering_prompt}}') {
            list.push({
                type: 'genai-prompt-mismatch',
                message: 'GenAI prompt override is not set to standard new prompt for gemini-3.5-flash.',
                level: 'p1'
            });
        }
    }

    if (!app.isLive) {
        list.forEach(w => {
            if (w.level === 'p1') {
                w.level = 'p2';
            }
        });
    }

    return list;
};

const getTelephonyStatus = (status: LocationStatus): string => {
    if (!status.rawDeployments || status.rawDeployments.length === 0) return 'Not Configured';

    const hasEsInbound = status.rawDeployments.some(d => !d.useForOutboundCalling && (d.locale || '').toLowerCase().startsWith('es'));
    const hasEsOutbound = status.rawDeployments.some(d => d.useForOutboundCalling && (d.locale || '').toLowerCase().startsWith('es'));

    const hasEnInbound = status.rawDeployments.some(d => !d.useForOutboundCalling && !(d.locale || '').toLowerCase().startsWith('es'));
    const hasEnOutbound = status.rawDeployments.some(d => d.useForOutboundCalling && !(d.locale || '').toLowerCase().startsWith('es'));

    // Case 1: Inbound & Outbound configured
    const isBothInboundAndOutbound = (hasEsInbound || hasEnInbound) && (hasEsOutbound || hasEnOutbound);
    if (isBothInboundAndOutbound) {
        if (hasEsInbound || hasEsOutbound) {
            return 'Inbound & Outbound (EN & ES)';
        }
        return 'Inbound & Outbound (EN only)';
    }

    // Case 2: Inbound only configured
    const isInboundOnly = (hasEsInbound || hasEnInbound);
    if (isInboundOnly) {
        if (hasEsInbound) return 'Inbound (EN & ES)';
        return 'Inbound (EN only)';
    }

    // Case 3: Outbound only configured
    const isOutboundOnly = (hasEsOutbound || hasEnOutbound);
    if (isOutboundOnly) {
        if (hasEsOutbound) return 'Outbound (EN & ES)';
        return 'Outbound (EN only)';
    }

    return 'Not Configured';
};

const DropdownFilter: React.FC<{
    title: string;
    value: string;
    options: { label: string; value: string }[];
    onChange: (val: string) => void;
    isActive?: boolean;
    alignRight?: boolean;
}> = ({ title, value, options, onChange, isActive, alignRight }) => {
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        const handleOutsideClick = () => setIsOpen(false);
        document.addEventListener('click', handleOutsideClick);
        return () => document.removeEventListener('click', handleOutsideClick);
    }, [isOpen]);

    const activeOption = options.find(o => o.value === value);
    const displayText = activeOption ? activeOption.label : title;

    return (
        <DropdownWrapper onClick={(e) => e.stopPropagation()}>
            <DropdownHeaderButton
                onClick={() => setIsOpen(!isOpen)}
                isActive={isActive || !!value}
            >
                <span>{displayText}</span>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" className="chevron">
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </DropdownHeaderButton>
            {isOpen && (
                <DropdownMenu alignRight={alignRight}>
                    <DropdownMenuItem
                        selected={value === ""}
                        onClick={() => { onChange(""); setIsOpen(false); }}
                    >
                        {title} (All)
                    </DropdownMenuItem>
                    {options.map(opt => (
                        <DropdownMenuItem
                            key={opt.value}
                            selected={value === opt.value}
                            onClick={() => { onChange(opt.value); setIsOpen(false); }}
                        >
                            {opt.label}
                        </DropdownMenuItem>
                    ))}
                </DropdownMenu>
            )}
        </DropdownWrapper>
    );
};

const FormDropdown: React.FC<{
    placeholder: string;
    value: string;
    options: { label: string; value: string }[];
    onChange: (val: string) => void;
    disabled?: boolean;
}> = ({ placeholder, value, options, onChange, disabled }) => {
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        const handleOutsideClick = () => setIsOpen(false);
        document.addEventListener('click', handleOutsideClick);
        return () => document.removeEventListener('click', handleOutsideClick);
    }, [isOpen]);

    const activeOption = options.find(o => o.value === value);
    const displayText = activeOption ? activeOption.label : placeholder;

    return (
        <FormDropdownWrapper onClick={(e) => e.stopPropagation()}>
            <FormDropdownButton
                type="button"
                onClick={() => !disabled && setIsOpen(!isOpen)}
                disabled={disabled}
                hasValue={!!value}
            >
                <span>{displayText}</span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="chevron">
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </FormDropdownButton>
            {isOpen && (
                <FormDropdownMenu>
                    {options.map(opt => (
                        <FormDropdownMenuItem
                            key={opt.value}
                            selected={value === opt.value}
                            onClick={() => { onChange(opt.value); setIsOpen(false); }}
                        >
                            {opt.label}
                        </FormDropdownMenuItem>
                    ))}
                </FormDropdownMenu>
            )}
        </FormDropdownWrapper>
    );
};

interface SparkOrganizationManagementSettingsProps {
    organizationId: string;
    locationApps?: ApplicationModel[];
    commonApp?: ApplicationModel;
    appliedTemplateContainer?: AppliedApplicationTemplateContainer;
    applicationEnvironmentsContainer?: ApplicationEnvironmentsContainer;
    appContainer?: ApplicationContainer;
}

interface ConnectionProfile {
    phoneNumber: string;
    asrProvider: string;
    proactiveResponsePrompt: string;
    ttsProvider: string;
    voiceString: string;
    friendlyName: string;
}

interface LocationStatus {
    lastSync: string | null;
    lastPublish: string | null;
    status: 'Idle' | 'Syncing...' | 'Publishing...' | 'Complete' | 'Failed' | null;
    hasModules: boolean;
    todaysHours: string | null;
    transferTarget: string | null;
    timeZones: string[];
    connections: ConnectionProfile[];
    rawDeployments: TelephonyDeploymentResponse[];
    disabledDaysRaw?: string | null;
    genAiModelRaw?: string | null;
    posConfigIssues?: string[];
    isLoading?: boolean;
}

const SparkOrganizationManagementSettings: React.FC<SparkOrganizationManagementSettingsProps> = ({
    organizationId,
    locationApps: passedLocationApps = [],
    commonApp,
    appliedTemplateContainer,
    applicationEnvironmentsContainer,
    appContainer
}) => {
    const variablesContainer = OrganizationVariablesContainer.useContainer();
    const isMounted = React.useRef(true);
    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    const [asrContext, setAsrContext] = useState('');
    const [isLoaded, setIsLoaded] = useState(false);
    const [hasInitialized, setHasInitialized] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Collapsible states
    const [isAsrContextCollapsed, setIsAsrContextCollapsed] = useState(true);
    const [isOtherConstantsCollapsed, setIsOtherConstantsCollapsed] = useState(true);
    const [isPublishStatusCollapsed, setIsPublishStatusCollapsed] = useState(false);

    // Toast alerts
    const [errors, setErrors] = useState<string[]>([]);
    const [successes, setSuccesses] = useState<string[]>([]);
    const [warnings, setWarnings] = useState<string[]>([]);

    // Edit Modal States
    const [editingDeployment, setEditingDeployment] = useState<TelephonyDeploymentResponse | null>(null);
    const [editingAppId, setEditingAppId] = useState<string>('');
    const [editType, setEditType] = useState<'asr' | 'proactive' | 'voice' | 'genaiModel' | 'genaiPrompt' | 'genai' | null>(null);
    const [editAsrProvider, setEditAsrProvider] = useState<string>('');
    const [editProactivePrompt, setEditProactivePrompt] = useState<string>('');
    const [editVoiceString, setEditVoiceString] = useState<string>('');
    const [editGenAIModel, setEditGenAIModel] = useState<string>('');
    const [editGenAIPrompt, setEditGenAIPrompt] = useState<string>('');
    const [isSavingTelemetry, setIsSavingTelemetry] = useState<boolean>(false);
    const [isLoadingOnDemand, setIsLoadingOnDemand] = useState<boolean>(false);

    const startEditAsr = (appId: string, deployment: TelephonyDeploymentResponse) => {
        setEditingAppId(appId);
        setEditingDeployment(deployment);
        setEditType('asr');
        setEditAsrProvider(deployment.asrProvider || 'Deepgram');
    };

    const startEditProactive = (appId: string, deployment: TelephonyDeploymentResponse) => {
        setEditingAppId(appId);
        setEditingDeployment(deployment);
        setEditType('proactive');
        setEditProactivePrompt(deployment.generativeProactiveResponsePrompt || '');
    };

    const startEditVoice = (appId: string, deployment: TelephonyDeploymentResponse) => {
        setEditingAppId(appId);
        setEditingDeployment(deployment);
        setEditType('voice');
        setEditVoiceString(deployment.ttsOptions?.[0]?.ttsVoice || deployment.ttsVoice || '');
    };

    const startEditGenAIModel = async (appId: string, currentApp: ApplicationModel) => {
        setEditingAppId(appId);
        setEditType('genai');
        setIsLoadingOnDemand(true);
        setErrors([]);

        const appType = getAppType(currentApp);

        try {
            if (appType === 'Healthcare') {
                const status = locationStatuses[appId];
                const sharedApp = resolvedLocationApps.find(a => a.allowsTemplating);
                const sharedStatus = sharedApp ? locationStatuses[sharedApp.id!] : undefined;
                const fallbackVal = status?.genAiModelRaw || (sharedStatus ? sharedStatus.genAiModelRaw || '' : '');
                setEditGenAIModel(fallbackVal || 'openai-gpt-4o');
            } else {
                const detail = await voicifyApi.findApplication(appId);
                if (detail?.resultType === 'Ok' && detail.data) {
                    setResolvedLocationApps(prev => prev.map(a => a.id === appId ? detail.data! : a));
                    const modelVal = getFeatureFlagSettingValue(detail.data, FoodOrderingGenAIModelSettingId) || 'gemini-2.5-flash';
                    const promptVal = getFeatureFlagSettingValue(detail.data, FoodOrderingGenAIPromptOverrideSettingId) || '';
                    const finalPromptVal = promptVal === '{{constant.base_ordering_prompt}}' ? '{{constant.new_ordering_prompt}}' : promptVal;
                    setEditGenAIModel(modelVal);
                    setEditGenAIPrompt(finalPromptVal);
                } else {
                    setErrors(['Failed to load application GenAI settings.']);
                }
            }
        } catch (err) {
            console.error('Failed to load application on-demand details:', err);
            setErrors(['Error loading GenAI settings.']);
        } finally {
            setIsLoadingOnDemand(false);
        }
    };

    const startEditGenAIPrompt = async (appId: string, currentApp: ApplicationModel) => {
        setEditingAppId(appId);
        setEditType('genaiPrompt');
        setIsLoadingOnDemand(true);
        setErrors([]);

        const targetAppId = appId;

        try {
            const detail = await voicifyApi.findApplication(targetAppId);
            if (detail?.resultType === 'Ok' && detail.data) {
                // Merge the loaded featureFlags onto the local location app state in memory
                setResolvedLocationApps(prev => prev.map(a => a.id === appId ? detail.data! : a));
                const val = getFeatureFlagSettingValue(detail.data, FoodOrderingGenAIPromptOverrideSettingId) || '';
                const finalVal = val === '{{constant.base_ordering_prompt}}' ? '{{constant.new_ordering_prompt}}' : val;
                setEditGenAIPrompt(finalVal);
            } else {
                setErrors(['Failed to load application GenAI settings.']);
            }
        } catch (err) {
            console.error('Failed to load application on-demand details:', err);
            setErrors(['Error loading GenAI settings.']);
        } finally {
            setIsLoadingOnDemand(false);
        }
    };

    const handleSaveTelemetrySettings = async () => {
        if (!editingAppId || !editType) return;
        setIsSavingTelemetry(true);
        setErrors([]);
        setSuccesses([]);

        try {
            if (editType === 'genai' || editType === 'genaiModel' || editType === 'genaiPrompt') {
                const matchedApp = resolvedLocationApps.find(a => a.id === editingAppId);
                if (!matchedApp) throw new Error('Application not found.');
                const appType = getAppType(matchedApp);
                const targetAppId = editingAppId;

                if (appType === 'Healthcare') {
                    // Update module field for Dental
                    let genAIProviderVar = 'genAIProvider';
                    let fieldExistsOnLocation = false;
                    if (appliedTemplateContainer) {
                        try {
                            const templatesResult = await appliedTemplateContainer.loadAppliedTemplates(targetAppId);
                            if (templatesResult?.resultType === 'Ok' && templatesResult.data) {
                                for (const template of templatesResult.data) {
                                    const allFieldValues = [
                                        ...(template.templateFormFieldValues || []),
                                        ...(template.dynamicFormFieldValuesCache || [])
                                    ];
                                    const found = allFieldValues.find(fv => {
                                        const assocVar = (fv.associatedVariable || '').trim();
                                        return assocVar === 'genAIProvider' || assocVar === '{genAIProvider}';
                                    });
                                    if (found) {
                                        genAIProviderVar = found.associatedVariable!.trim();
                                        fieldExistsOnLocation = true;
                                        break;
                                    }
                                }
                            }
                        } catch (err) {
                            console.error('Failed to resolve exact genAIProvider variable:', err);
                        }
                    }

                    if (!fieldExistsOnLocation && !matchedApp.allowsTemplating) {
                        const sharedApp = resolvedLocationApps.find(a => a.allowsTemplating);
                        if (sharedApp) {
                            setErrors([`Failed to update GenAI Provider for ${matchedApp.name}: This field lives in the Shared Configuration. Select the Shared Configuration to update GenAI Provider for all locations.`]);
                            setIsSavingTelemetry(false);
                            return;
                        }
                    }

                    const result = await updateApplicationModuleField(targetAppId, genAIProviderVar, editGenAIModel);
                    if (result.success) {
                        setSuccesses([`Successfully updated GenAI Provider field for ${matchedApp.name}!`]);
                        setLocationStatuses(prev => ({
                            ...prev,
                            [targetAppId]: {
                                ...prev[targetAppId],
                                genAiModelRaw: editGenAIModel
                            }
                        }));

                        if (appliedTemplateContainer) {
                            const updateTemplatesList = (templates: any[]) => {
                                return templates.map(t => ({
                                    ...t,
                                    templateFormFieldValues: (t.templateFormFieldValues || []).map((fv: any) => {
                                        const assocVar = (fv.associatedVariable || '').trim();
                                        if (assocVar === 'genAIProvider' || assocVar === '{genAIProvider}') {
                                            return { ...fv, value: editGenAIModel };
                                        }
                                        return fv;
                                    }),
                                    dynamicFormFieldValuesCache: (t.dynamicFormFieldValuesCache || []).map((fv: any) => {
                                        const assocVar = (fv.associatedVariable || '').trim();
                                        if (assocVar === 'genAIProvider' || assocVar === '{genAIProvider}') {
                                            return { ...fv, value: editGenAIModel };
                                        }
                                        return fv;
                                    })
                                }));
                            };

                            const newPerApp = appliedTemplateContainer.state.perApplicationAppliedTemplates.map(p => {
                                if (p.applicationId === targetAppId) {
                                    return {
                                        ...p,
                                        appliedTemplates: updateTemplatesList(p.appliedTemplates)
                                    };
                                }
                                return p;
                            });

                            appliedTemplateContainer.setState({
                                ...appliedTemplateContainer.state,
                                perApplicationAppliedTemplates: newPerApp,
                                appliedTemplates: targetAppId === editingAppId ? updateTemplatesList(appliedTemplateContainer.state.appliedTemplates) : appliedTemplateContainer.state.appliedTemplates
                            });
                        }

                        closeTelemetryModal();
                    } else {
                        if (!matchedApp.allowsTemplating) {
                            setErrors([`Failed to update GenAI Provider for ${matchedApp.name}: This field lives in the Shared Configuration. Select the Shared Configuration to update GenAI Provider for all locations.`]);
                        } else {
                            setErrors([`Failed to update GenAI Provider field: ${result.error || 'Unknown API error'}`]);
                        }
                    }
                } else {
                    // 1. Fetch the master feature flags list to get the true, valid database schema
                    const masterFlagsResult = await voicifyApi.getFeatureFlags();
                    if (masterFlagsResult?.resultType !== 'Ok') throw new Error('Failed to retrieve master feature flag schemas.');

                    const masterFlag = masterFlagsResult.data?.find(f => f.id === GenAIFoodOrdering);
                    if (!masterFlag) throw new Error('GenAI Food Ordering feature flag schema not found on database.');

                    const appFlag = matchedApp.featureFlags?.find(f => f.id === GenAIFoodOrdering);

                    let modelVal: string | undefined = undefined;
                    let promptVal: string | undefined = undefined;

                    if (editType === 'genaiModel') {
                        modelVal = editGenAIModel;
                    } else if (editType === 'genaiPrompt') {
                        promptVal = editGenAIPrompt;
                    } else if (editType === 'genai') {
                        modelVal = editGenAIModel;
                        promptVal = editGenAIPrompt;
                    }

                    const requestPayload = buildFeatureFlagUpdateRequest(appFlag, masterFlag, modelVal, promptVal);

                    const response = await voicifyApi.updateApplicationFeatureFlagSettings(targetAppId, requestPayload);

                    if (response?.resultType === 'Ok') {
                        setSuccesses([`Successfully updated GenAI feature flag settings for ${matchedApp.name}!`]);

                        // Merge the newly updated featureFlags list onto the local Location App state in memory
                        setResolvedLocationApps(prev => prev.map(a =>
                            a.id === editingAppId ? { ...a, featureFlags: response.data!.featureFlags } : a
                        ));
                        closeTelemetryModal();
                    } else if (response?.errors) {
                        setErrors(response.errors);
                    } else {
                        setErrors(['Failed to update GenAI settings.']);
                    }
                }
            } else if (editingDeployment) {
                let request = buildTelephonyUpdateRequest(editingDeployment, editType, {
                    editAsrProvider,
                    editProactivePrompt,
                    editVoiceString
                });

                const response = await voicifyApi.updateTelephonyDeployment(editingAppId, editingDeployment.phoneNumber, request);

                if (response?.resultType === 'Ok') {
                    setSuccesses([`Successfully updated connection settings for ${editingDeployment.phoneNumber}!`]);

                    // Update the active modal selection state natively so it instantly reflects the change in the UI
                    // (protecting the phone number from backend caller-id bleeding)
                    setEditingDeployment({ ...editingDeployment, ...response.data!, phoneNumber: editingDeployment.phoneNumber });

                    // Optimistically update the single connection status locally in React memory instantly
                    setLocationStatuses(prev => {
                        const currentStatus = prev[editingAppId];
                        if (!currentStatus) return prev;

                        const safeResponse = response.data || {};

                        // We must explicitly extract defined values from the response so we don't accidentally overwrite critical local
                        // state variables (like `useForOutboundCalling` or `locale`) with undefined/null if the API returns a sparse model!
                        const safeResponseCleaned = Object.fromEntries(Object.entries(safeResponse).filter(([_, v]) => v != null));

                        // Backend bug guard: Sometimes the backend API returns the Inbound Caller ID's deployment when updating 
                        // an outbound line! We MUST forcefully protect the phone number from being corrupted by the API response.
                        if (safeResponseCleaned.phoneNumber !== request.phoneNumber && request.phoneNumber) {
                            safeResponseCleaned.phoneNumber = request.phoneNumber;
                        }

                        const updatedRawDeployments = (currentStatus.rawDeployments || []).map(d => {
                            // We MUST match strictly by exact deployment ID if it exists, or exact match of ALL keys 
                            // because a single phone number can have multiple rows (Inbound EN, Outbound EN)
                            // However, updateTelephonyDeployment API operates on phoneNumber, so it might update ALL.
                            // BUT we should only optimistically merge onto the EXACT matching row.

                            // Let's match by ID first, then fallback to phone + useForOutboundCalling + locale
                            const isExactMatch = d.id
                                ? d.id === editingDeployment.id
                                : (d.phoneNumber === editingDeployment.phoneNumber &&
                                    d.useForOutboundCalling === editingDeployment.useForOutboundCalling &&
                                    d.locale === editingDeployment.locale);

                            if (isExactMatch) {
                                return {
                                    ...d,
                                    ...safeResponseCleaned,
                                    ...request // Request always has the guaranteed final payload
                                };
                            }
                            return d;
                        });

                        const updatedConnections = (currentStatus.connections || []).map(conn => {
                            if (conn.phoneNumber === editingDeployment.phoneNumber) {
                                const mergedData = { ...conn, ...safeResponseCleaned, ...request };
                                const provider = mergedData.ttsOptions?.[0]?.ttsProvider || mergedData.ttsProvider || 'N/A';
                                const voiceString = mergedData.ttsOptions?.[0]?.ttsVoice || mergedData.ttsVoice || '';
                                return {
                                    ...conn,
                                    phoneNumber: mergedData.phoneNumber || 'N/A',
                                    asrProvider: mergedData.asrProvider || 'N/A',
                                    proactiveResponsePrompt: mergedData.generativeProactiveResponsePrompt || 'None',
                                    ttsProvider: provider,
                                    voiceString,
                                    friendlyName: getVoiceFriendlyName(voiceString)
                                };
                            }
                            return conn;
                        });

                        return {
                            ...prev,
                            [editingAppId]: {
                                ...currentStatus,
                                rawDeployments: updatedRawDeployments,
                                connections: updatedConnections
                            }
                        };
                    });

                    closeTelemetryModal();
                } else if (response?.errors) {
                    setErrors(response.errors);
                } else {
                    setErrors(['Failed to update connection settings.']);
                }
            }
        } catch (err) {
            console.error('Failed to update telephony settings:', err);
            setErrors([`Error saving connection settings: ${err.message || err.toString()}`]);
        } finally {
            setIsSavingTelemetry(false);
        }
    };

    const closeTelemetryModal = () => {
        setEditingDeployment(null);
        setEditingAppId('');
        setEditType(null);
        setEditAsrProvider('');
        setEditProactivePrompt('');
        setEditVoiceString('');
        setEditGenAIModel('');
        setEditGenAIPrompt('');
    };

    // Dynamically resolved apps (locations & shared topics) inside the component
    const [resolvedLocationApps, setResolvedLocationApps] = useState<ApplicationModel[]>([]);
    const organizationHasDentalApplication = resolvedLocationApps.some(app => getAppType(app) === 'Healthcare');
    const [locationStatuses, setLocationStatuses] = useState<Record<string, LocationStatus>>({});
    const [isLoadingStatus, setIsLoadingStatus] = useState(false);
    const [isActionRequiredExpanded, setIsActionRequiredExpanded] = useState(false);

    // Bulk operations hooks
    const {
        isProcessing: isBulkProcessing,
        logs: bulkLogs,
        syncApps,
        publishApps,
        clearLogs
    } = useBulkAppOperations({
        appliedTemplateContainer,
        applicationEnvironmentsContainer
    });

    // Checkbox multi-select, search, and custom sorting states
    const [selectedAppIds, setSelectedAppIds] = useState<Set<string>>(new Set());
    const [activeDetailAppId, setActiveDetailAppId] = useState<string | null>(null);
    const [filters, setFilters] = useState({
        name: '',
        isLive: '', // '', 'live', 'draft', 'shared'
        warningState: '', // '', 'warnings', 'no-warnings'
        asr: '', // '', 'Deepgram', 'Soniox', 'Google', 'mixed', 'none'
        proactive: '', // '', 'standard', 'custom', 'disabled', 'outbound_active'
        voice: '', // '', [voice_id], 'mixed', 'none'
        model: '', // '', 'none', [model_value]
        lastSync: '',
        lastPublish: '',
    });

    const [sort, setSort] = useState<{
        column: 'name' | 'isLive' | 'asr' | 'proactive' | 'voice' | 'lastSync' | 'lastPublish' | 'hours' | 'target' | 'warnings' | 'model' | null;
        direction: 'asc' | 'desc' | null;
    }>({ column: null, direction: null });

    const handleFilterChange = (column: keyof typeof filters, value: string) => {
        setFilters(prev => ({
            ...prev,
            [column]: value
        }));
    };

    // Bulk update states
    const [isBulkUpdating, setIsBulkUpdating] = useState(false);
    const [bulkUpdatingField, setBulkUpdatingField] = useState<string | null>(null);
    const [bulkLiveValue, setBulkLiveValue] = useState('');
    const [bulkTargetLaunchDate, setBulkTargetLaunchDate] = useState('');
    const [bulkVoiceValue, setBulkVoiceValue] = useState('');
    const [bulkVoiceEnglishValue, setBulkVoiceEnglishValue] = useState('');
    const [bulkVoiceSpanishValue, setBulkVoiceSpanishValue] = useState('');
    const [bulkAsrValue, setBulkAsrValue] = useState('');
    const [bulkProactiveMode, setBulkProactiveMode] = useState('');
    const [bulkProactiveValue, setBulkProactiveValue] = useState('');
    const [bulkClosedDaysValue, setBulkClosedDaysValue] = useState('');
    const [bulkClosedDaysDates, setBulkClosedDaysDates] = useState<string[]>([]);
    const [bulkGenAIModel, setBulkGenAIModel] = useState('');

    const handleBulkUpdate = async (field: 'voice' | 'asr' | 'proactive' | 'live' | 'closedDays' | 'genaiModel', value: any) => {
        setIsBulkUpdating(true);
        setBulkUpdatingField(field);
        setErrors([]);
        setSuccesses([]);
        setWarnings([]);

        const selectedIds = Array.from(selectedAppIds);
        const collectedWarnings: string[] = [];
        const skippedSpanishNames: string[] = [];
        let skippedOutbound = false;

        // Pre-flight check for telephony updates on locations with no phone numbers
        const validIds: string[] = [];
        if (field === 'voice' || field === 'asr' || field === 'proactive') {
            const missingPhoneNames: string[] = [];
            for (const appId of selectedIds) {
                const app = resolvedLocationApps.find(a => a.id === appId);
                if (!app) continue;
                if (app.allowsTemplating) {
                    missingPhoneNames.push(app.name || 'Shared Configuration');
                    continue;
                }

                const status = locationStatuses[appId];
                if (!status || !status.rawDeployments || status.rawDeployments.length === 0) {
                    missingPhoneNames.push(app.name || 'Unknown Location');
                } else {
                    const hasValidLine = status.rawDeployments.some(dep => dep.phoneNumber);
                    if (!hasValidLine) {
                        missingPhoneNames.push(app.name || 'Unknown Location');
                    } else {
                        validIds.push(appId);
                    }
                }
            }

            if (missingPhoneNames.length > 0) {
                collectedWarnings.push(`Telephony settings were skipped for locations with missing phone numbers: ${missingPhoneNames.join(', ')}. Add a phone number in Spark to enable.`);
            }
        } else {
            validIds.push(...selectedIds);
        }

        let successCount = 0;
        let failCount = 0;
        let successNames: string[] = [];

        for (const appId of validIds) {
            const app = resolvedLocationApps.find(a => a.id === appId);
            const status = locationStatuses[appId];
            if (!app) continue;

            try {
                if (field === 'live') {
                    const payload = buildBulkLiveUpdateRequest(app, value);

                    const result = await voicifyApi.updateApplication(appId, payload);
                    if (result.resultType === 'Ok') {
                        // Update app locally
                        setResolvedLocationApps(prev => prev.map(a => a.id === appId ? result.data! : a));
                        successCount++;
                        successNames.push(app.name || 'Unknown Location');
                    } else {
                        failCount++;
                    }
                } else if (field === 'closedDays') {
                    // value is an array of date strings
                    const newDates: string[] = Array.isArray(value) ? value : [];
                    const existingRaw = resolveExistingClosedDays(appId, locationStatuses, resolvedLocationApps);

                    const finalValue = buildClosedDaysUpdateRequest(existingRaw, newDates);

                    const result = await updateApplicationModuleField(appId, 'DISABLED_DAYS', finalValue);
                    if (result.success) {
                        successCount++;
                        successNames.push(app.name || 'Unknown Location');
                    } else {
                        failCount++;
                        if (!app.allowsTemplating) {
                            setErrors(prev => {
                                const msg = `Failed to update Closed Days for ${app.name}: This field lives in the Shared Configuration. Select the Shared Configuration to update closed days for all locations.`;
                                if (!prev.includes(msg)) return [...prev, msg];
                                return prev;
                            });
                        } else {
                            setErrors(prev => [...prev, `Failed to update Closed Days for ${app.name}: ${result.error || 'Unknown API error'}`]);
                        }
                    }
                } else if (field === 'genaiModel') {
                    const appType = getAppType(app);
                    if (appType === 'Healthcare') {
                        let genAIProviderVar = 'genAIProvider';
                        let fieldExistsOnLocation = false;
                        if (appliedTemplateContainer) {
                            try {
                                const templatesResult = await appliedTemplateContainer.loadAppliedTemplates(appId);
                                const resolved = resolveGenAIProviderVar(templatesResult);
                                genAIProviderVar = resolved.genAIProviderVar;
                                fieldExistsOnLocation = resolved.fieldExistsOnLocation;
                            } catch (err) {
                                console.error('Failed to resolve exact genAIProvider variable:', err);
                            }
                        }

                        if (!fieldExistsOnLocation && !app.allowsTemplating) {
                            const sharedApp = resolvedLocationApps.find(a => a.allowsTemplating);
                            if (sharedApp) {
                                setErrors(prev => {
                                    const msg = `Failed to update GenAI Provider for ${app.name}: This field lives in the Shared Configuration. Select the Shared Configuration to update GenAI Provider for all locations.`;
                                    if (!prev.includes(msg)) return [...prev, msg];
                                    return prev;
                                });
                                failCount++;
                                continue;
                            }
                        }

                        const result = await updateApplicationModuleField(appId, genAIProviderVar, value);
                        if (result.success) {
                            successCount++;
                            successNames.push(app.name || 'Unknown Location');

                            // Update local location statuses
                            setLocationStatuses(prev => ({
                                ...prev,
                                [appId]: {
                                    ...prev[appId],
                                    genAiModelRaw: value
                                }
                            }));

                            // Update in-memory appliedTemplateContainer cache
                            if (appliedTemplateContainer) {
                                const updateTemplatesList = (templates: any[]) => {
                                    return templates.map(t => ({
                                        ...t,
                                        templateFormFieldValues: (t.templateFormFieldValues || []).map((fv: any) => {
                                            const assocVar = (fv.associatedVariable || '').trim();
                                            if (assocVar === 'genAIProvider' || assocVar === '{genAIProvider}') {
                                                return { ...fv, value: value };
                                            }
                                            return fv;
                                        }),
                                        dynamicFormFieldValuesCache: (t.dynamicFormFieldValuesCache || []).map((fv: any) => {
                                            const assocVar = (fv.associatedVariable || '').trim();
                                            if (assocVar === 'genAIProvider' || assocVar === '{genAIProvider}') {
                                                return { ...fv, value: value };
                                            }
                                            return fv;
                                        })
                                    }));
                                };

                                const newPerApp = appliedTemplateContainer.state.perApplicationAppliedTemplates.map(p => {
                                    if (p.applicationId === appId) {
                                        return {
                                            ...p,
                                            appliedTemplates: updateTemplatesList(p.appliedTemplates)
                                        };
                                    }
                                    return p;
                                });

                                appliedTemplateContainer.setState({
                                    ...appliedTemplateContainer.state,
                                    perApplicationAppliedTemplates: newPerApp
                                });
                            }
                        } else {
                            failCount++;
                            if (!app.allowsTemplating) {
                                setErrors(prev => {
                                    const msg = `Failed to update GenAI Provider for ${app.name}: This field lives in the Shared Configuration. Select the Shared Configuration to update GenAI Provider for all locations.`;
                                    if (!prev.includes(msg)) return [...prev, msg];
                                    return prev;
                                });
                            } else {
                                setErrors(prev => [...prev, `Failed to update GenAI Provider for ${app.name}: ${result.error || 'Unknown API error'}`]);
                            }
                        }
                    } else {
                        try {
                            const detail = await voicifyApi.findApplication(appId);
                            if (detail?.resultType === 'Ok' && detail.data) {
                                const masterFlagsResult = await voicifyApi.getFeatureFlags();
                                const masterFlag = masterFlagsResult?.data?.find(f => f.id === GenAIFoodOrdering);
                                if (masterFlag) {
                                    const appFlag = detail.data.featureFlags?.find(f => f.id === GenAIFoodOrdering);
                                    const requestPayload = buildFeatureFlagUpdateRequest(appFlag, masterFlag, value);
                                    const response = await voicifyApi.updateApplicationFeatureFlagSettings(appId, requestPayload);
                                    if (response?.resultType === 'Ok') {
                                        successCount++;
                                        successNames.push(app.name || 'Unknown Location');
                                        setResolvedLocationApps(prev => prev.map(a =>
                                            a.id === appId ? { ...a, featureFlags: response.data!.featureFlags } : a
                                        ));
                                    } else {
                                        failCount++;
                                    }
                                } else {
                                    failCount++;
                                }
                            } else {
                                failCount++;
                            }
                        } catch {
                            failCount++;
                        }
                    }
                } else {
                    // Telephony fields: voice, asr, proactive
                    if (status && status.rawDeployments && status.rawDeployments.length > 0) {
                        let appSuccess = true;

                        if (field === 'voice' && value && typeof value === 'object' && value.isCustom) {
                            const hasSpanishDeployment = status.rawDeployments.some(dep => (dep.locale || '').toLowerCase().startsWith('es'));
                            if (!hasSpanishDeployment) {
                                skippedSpanishNames.push(app.name || 'Unknown Location');
                            }
                        }

                        for (const dep of status.rawDeployments) {
                            if (!dep.phoneNumber) continue; // Skip updating telephony settings if there is no phone number
                            if (field === 'proactive' && value === 'standard' && dep.useForOutboundCalling === true) {
                                skippedOutbound = true;
                                continue;
                            }
                            const updatedRequest: any = {
                                ...dep,
                                ttsVoice: dep.ttsOptions?.[0]?.ttsVoice || dep.ttsVoice || '',
                                ttsProvider: dep.ttsOptions?.[0]?.ttsProvider || dep.ttsProvider || 'Deepgram',
                                asrProvider: dep.asrProvider || 'Deepgram',
                                useGenerativeProactiveResponse: dep.useGenerativeProactiveResponse,
                                useProactiveResponses: dep.useProactiveResponses,
                                generativeProactiveResponsePrompt: dep.generativeProactiveResponsePrompt || '',
                                proactiveResponses: dep.proactiveResponses || ''
                            };

                            if (field === 'voice') {
                                Object.assign(updatedRequest, buildBulkVoiceUpdateRequest(updatedRequest, value));
                            } else if (field === 'asr') {
                                updatedRequest.asrProvider = value;
                            } else if (field === 'proactive') {
                                if (value === 'standard') {
                                    const appType = getAppType(app);
                                    const isDental = appType === 'Healthcare';
                                    const isOutbound = dep.useForOutboundCalling === true;

                                    if (isOutbound) {
                                        updatedRequest.useGenerativeProactiveResponse = false;
                                        updatedRequest.useProactiveResponses = false;
                                        updatedRequest.generativeProactiveResponsePrompt = '';
                                        updatedRequest.proactiveResponses = '';
                                    } else {
                                        const isSpanish = (dep.locale || '').toLowerCase().startsWith('es');
                                        const expected = isDental
                                            ? (isSpanish ? '{{constant.generative_proactive_response_dental_spanish}}' : '{{constant.generative_proactive_response_dental}}')
                                            : (isSpanish ? '{{constant.generative_proactive_response_ordering_spanish}}' : '{{constant.proactive_response_prompt}}');

                                        updatedRequest.useGenerativeProactiveResponse = true;
                                        updatedRequest.generativeProactiveResponsePrompt = expected;
                                        updatedRequest.useProactiveResponses = true;
                                        updatedRequest.proactiveResponses = '';
                                    }
                                } else {
                                    // Custom value or disable
                                    const hasValue = !!value;
                                    updatedRequest.useGenerativeProactiveResponse = hasValue;
                                    updatedRequest.generativeProactiveResponsePrompt = value || '';
                                    updatedRequest.useProactiveResponses = hasValue;
                                    updatedRequest.proactiveResponses = '';
                                }
                            }

                            const depResult = await voicifyApi.updateTelephonyDeployment(appId, dep.phoneNumber, updatedRequest);
                            if (depResult?.resultType !== 'Ok') {
                                appSuccess = false;
                            }
                        }
                        if (appSuccess) {
                            successCount++;
                            successNames.push(app.name || 'Unknown Location');
                        } else {
                            failCount++;
                        }
                    } else {
                        successCount++;
                        successNames.push(app.name || 'Unknown Location');
                    }
                }
            } catch (err) {
                failCount++;
            }
        }

        setIsBulkUpdating(false);
        setBulkUpdatingField(null);

        // Refresh statuses to immediately show the new settings
        await loadLocationStatuses(resolvedLocationApps.filter(a => selectedAppIds.has(a.id!)));

        if (skippedSpanishNames.length > 0) {
            collectedWarnings.push(`Spanish voice was skipped for locations without Spanish locale configured: ${skippedSpanishNames.join(', ')}.`);
        }
        if (skippedOutbound) {
            collectedWarnings.push("Outbound configurations were not updated with proactive responses.");
        }
        if (collectedWarnings.length > 0) {
            setWarnings(collectedWarnings);
        }

        // Reset inputs on success
        if (failCount === 0) {
            if (successCount > 0) {
                setSuccesses([field === 'genaiModel' ? `Gen AI model has been updated in the Gen AI Provider form field for ${successNames.join(', ')}.` : `Successfully bulk updated ${field === 'closedDays' ? 'closed days' : field} settings for ${successCount} selected locations!`]);
            }
            setBulkLiveValue('');
            setBulkTargetLaunchDate('');
            setBulkVoiceValue('');
            setBulkVoiceEnglishValue('');
            setBulkVoiceSpanishValue('');
            setBulkAsrValue('');
            setBulkProactiveMode('');
            setBulkProactiveValue('');
            setBulkClosedDaysValue('');
            setBulkGenAIModel('');
        } else {
            if (successCount > 0) {
                setSuccesses([field === 'genaiModel' ? `Gen AI model has been updated in the Gen AI Provider form field for ${successNames.join(', ')}.` : `Successfully bulk updated ${field === 'closedDays' ? 'closed days' : field} settings for ${successCount} selected locations!`]);
            }
            // Keep the specific errors we already logged instead of overwriting them with a generic message!
        }
    };

    const handleSort = (column: typeof sort.column) => {
        setSort(prev => {
            if (prev.column === column) {
                if (prev.direction === 'asc') return { column, direction: 'desc' };
                return { column: null, direction: null }; // Clear sort
            }
            return { column, direction: 'asc' };
        });
    };

    const toggleSelectApp = (appId: string) => {
        setSelectedAppIds(prev => {
            const next = new Set(prev);
            if (next.has(appId)) {
                next.delete(appId);
            } else {
                next.add(appId);
            }
            return next;
        });
    };

    const toggleSelectAll = (appsList: ApplicationModel[]) => {
        const currentlySelectedCountForVisible = appsList.filter(app => selectedAppIds.has(app.id!)).length;
        const allSelected = currentlySelectedCountForVisible === appsList.length && appsList.length > 0;

        setSelectedAppIds(prev => {
            const next = new Set(prev);
            appsList.forEach(app => {
                if (allSelected) {
                    next.delete(app.id!);
                } else {
                    next.add(app.id!);
                }
            });
            return next;
        });
    };

    const selectAllLive = () => {
        const liveAppIds = resolvedLocationApps
            .filter(app => app.isLive && !app.allowsTemplating)
            .map(app => app.id!);
        setSelectedAppIds(new Set(liveAppIds));
    };

    const sortApps = (appsList: ApplicationModel[]): ApplicationModel[] => {
        if (!sort.column || !sort.direction) {
            return [...appsList].sort((a, b) => {
                const aName = (a.name || '').toLowerCase();
                const bName = (b.name || '').toLowerCase();
                const aIsTest = aName.includes('test') || aName.includes('sandbox');
                const bIsTest = bName.includes('test') || bName.includes('sandbox');
                const aIsShared = !!a.allowsTemplating;
                const bIsShared = !!b.allowsTemplating;

                if (aIsTest && bIsTest) return aName.localeCompare(bName);
                if (aIsTest) return 1;
                if (bIsTest) return -1;

                if (aIsShared && bIsShared) return aName.localeCompare(bName);
                if (aIsShared) return 1;
                if (bIsShared) return -1;

                return aName.localeCompare(bName);
            });
        }

        return [...appsList].sort((a, b) => {
            const statusA = locationStatuses[a.id!] || { lastSync: null, lastPublish: null, status: 'Idle', hasModules: true, todaysHours: null, transferTarget: null, timeZones: [], connections: [], rawDeployments: [], isLoading: true };
            const statusB = locationStatuses[b.id!] || { lastSync: null, lastPublish: null, status: 'Idle', hasModules: true, todaysHours: null, transferTarget: null, timeZones: [], connections: [], rawDeployments: [], isLoading: true };
            const sharedApp = resolvedLocationApps.find(a => a.allowsTemplating);
            const sharedStatus = sharedApp ? locationStatuses[sharedApp.id!] : undefined;
            const warningsA = deriveRowWarnings(a, statusA, sharedStatus, organizationHasDentalApplication).length;
            const warningsB = deriveRowWarnings(b, statusB, sharedStatus, organizationHasDentalApplication).length;

            let valA: any = '';
            let valB: any = '';

            switch (sort.column) {
                case 'name':
                    valA = (a.name || '').toLowerCase();
                    valB = (b.name || '').toLowerCase();
                    break;
                case 'isLive': {
                    const weightA = a.allowsTemplating ? 3 : (a.isLive ? 1 : 2);
                    const weightB = b.allowsTemplating ? 3 : (b.isLive ? 1 : 2);
                    if (weightA !== weightB) {
                        valA = weightA;
                        valB = weightB;
                    } else {
                        if (weightA === 1) { // Both Live
                            const dateA = a.launchDate ? new Date(a.launchDate).getTime() : 0;
                            const dateB = b.launchDate ? new Date(b.launchDate).getTime() : 0;
                            const res = dateB - dateA;
                            if (res !== 0) return sort.direction === 'asc' ? res : -res;
                            valA = 0; valB = 0;
                        } else if (weightA === 2) { // Both Draft
                            const dateA = a.targetLaunchDate ? new Date(a.targetLaunchDate).getTime() : Infinity;
                            const dateB = b.targetLaunchDate ? new Date(b.targetLaunchDate).getTime() : Infinity;
                            const res = dateA - dateB;
                            if (res !== 0) return sort.direction === 'asc' ? res : -res;
                            valA = 0; valB = 0;
                        } else {
                            valA = 0; valB = 0;
                        }
                    }
                    break;
                }
                case 'asr':
                    const uniqueAsrA = Array.from(new Set(statusA.rawDeployments?.map(d => d.asrProvider || 'Deepgram') || [])).sort();
                    const uniqueAsrB = Array.from(new Set(statusB.rawDeployments?.map(d => d.asrProvider || 'Deepgram') || [])).sort();
                    valA = uniqueAsrA.length === 0 ? 'zzz_not_configured' : (uniqueAsrA.length > 1 ? 'mixed_asr' : uniqueAsrA[0]);
                    valB = uniqueAsrB.length === 0 ? 'zzz_not_configured' : (uniqueAsrB.length > 1 ? 'mixed_asr' : uniqueAsrB[0]);
                    break;
                case 'proactive':
                    const listPA = (statusA.rawDeployments || []).map(d => {
                        const hasProactive = d.useGenerativeProactiveResponse || d.useProactiveResponses;
                        return hasProactive ? (d.generativeProactiveResponsePrompt || d.proactiveResponses || 'enabled') : 'disabled';
                    });
                    const uniquePA = Array.from(new Set(listPA)).sort();
                    valA = uniquePA.length === 0 ? 'zzz_not_configured' : (uniquePA.length > 1 ? 'mixed_proactive' : uniquePA[0]);

                    const listPB = (statusB.rawDeployments || []).map(d => {
                        const hasProactive = d.useGenerativeProactiveResponse || d.useProactiveResponses;
                        return hasProactive ? (d.generativeProactiveResponsePrompt || d.proactiveResponses || 'enabled') : 'disabled';
                    });
                    const uniquePB = Array.from(new Set(listPB)).sort();
                    valB = uniquePB.length === 0 ? 'zzz_not_configured' : (uniquePB.length > 1 ? 'mixed_proactive' : uniquePB[0]);
                    break;
                case 'voice':
                    const uniqueVoiceA = Array.from(new Set(statusA.rawDeployments?.map(d => extractVoiceId(d.ttsOptions?.[0]?.ttsVoice || d.ttsVoice || '')) || [])).sort();
                    const uniqueVoiceB = Array.from(new Set(statusB.rawDeployments?.map(d => extractVoiceId(d.ttsOptions?.[0]?.ttsVoice || d.ttsVoice || '')) || [])).sort();
                    valA = uniqueVoiceA.length === 0 ? 'zzz_not_configured' : (uniqueVoiceA.length > 1 ? 'mixed_voices' : uniqueVoiceA[0]);
                    valB = uniqueVoiceB.length === 0 ? 'zzz_not_configured' : (uniqueVoiceB.length > 1 ? 'mixed_voices' : uniqueVoiceB[0]);
                    break;

                case 'lastSync':
                    valA = statusA.lastSync ? new Date(statusA.lastSync).getTime() : 0;
                    valB = statusB.lastSync ? new Date(statusB.lastSync).getTime() : 0;
                    break;
                case 'lastPublish':
                    valA = statusA.lastPublish ? new Date(statusA.lastPublish).getTime() : 0;
                    valB = statusB.lastPublish ? new Date(statusB.lastPublish).getTime() : 0;
                    break;
                case 'hours':
                    valA = a.allowsTemplating ? 30000 : getEarliestStartMinutes(statusA.todaysHours);
                    valB = b.allowsTemplating ? 30000 : getEarliestStartMinutes(statusB.todaysHours);
                    break;
                case 'target':
                    valA = (statusA.transferTarget || '').toLowerCase();
                    valB = (statusB.transferTarget || '').toLowerCase();
                    break;
                case 'model':
                    const typeA = getAppType(a);
                    const typeB = getAppType(b);
                    valA = typeA === 'Healthcare' ? (statusA.genAiModelRaw || '').toLowerCase() : (getFeatureFlagSettingValue(a, FoodOrderingGenAIModelSettingId) || '').toLowerCase();
                    valB = typeB === 'Healthcare' ? (statusB.genAiModelRaw || '').toLowerCase() : (getFeatureFlagSettingValue(b, FoodOrderingGenAIModelSettingId) || '').toLowerCase();
                    break;
                case 'warnings':
                    const sharedApp = resolvedLocationApps.find(a => a.allowsTemplating);
                    const sharedStatus = sharedApp ? locationStatuses[sharedApp.id!] : undefined;
                    const listA = deriveRowWarnings(a, statusA, sharedStatus, organizationHasDentalApplication);
                    const listB = deriveRowWarnings(b, statusB, sharedStatus, organizationHasDentalApplication);
                    valA = listA.length === 0 ? 'A_no_warnings' : listA.map(w => w.type).sort().join(',');
                    valB = listB.length === 0 ? 'A_no_warnings' : listB.map(w => w.type).sort().join(',');
                    break;
            }

            if (valA === valB) return 0;
            if (valA === null || valA === undefined || valA === '') return 1;
            if (valB === null || valB === undefined || valB === '') return -1;

            const res = typeof valA === 'string' ? valA.localeCompare(valB) : (valA > valB ? 1 : -1);
            return sort.direction === 'asc' ? res : -res;
        });
    };

    const sortedApps = sortApps(resolvedLocationApps);
    const visibleApps = sortedApps.filter(app => {
        const status = locationStatuses[app.id!] || { lastSync: null, lastPublish: null, status: 'Idle', hasModules: true, todaysHours: null, transferTarget: null, timeZones: [], connections: [], rawDeployments: [] };
        const sharedApp = resolvedLocationApps.find(a => a.allowsTemplating);
        const sharedStatus = sharedApp ? locationStatuses[sharedApp.id!] : undefined;
        const warnings = deriveRowWarnings(app, status, sharedStatus, organizationHasDentalApplication);

        if (filters.name && !app.name?.toLowerCase().includes(filters.name.toLowerCase())) return false;

        if (filters.isLive === 'live' && (!app.isLive || app.allowsTemplating)) return false;
        if (filters.isLive === 'draft' && (app.isLive || app.allowsTemplating)) return false;
        if (filters.isLive === 'shared' && !app.allowsTemplating) return false;

        if (filters.warningState === 'warnings' && warnings.length === 0) return false;
        if (filters.warningState === 'no-warnings' && warnings.length > 0) return false;

        if (filters.asr) {
            if (filters.asr === 'mixed') {
                const uniqueAsr = Array.from(new Set(status.rawDeployments?.map(d => d.asrProvider || 'Deepgram') || []));
                if (uniqueAsr.length <= 1) return false;
            } else if (filters.asr === 'none') {
                if (status.rawDeployments && status.rawDeployments.length > 0) return false;
            } else {
                if (!status.rawDeployments?.some(d => (d.asrProvider || 'Deepgram') === filters.asr)) return false;
            }
        }

        if (filters.proactive) {
            const hasInboundProactive = status.rawDeployments?.some(d => !d.useForOutboundCalling && (d.useGenerativeProactiveResponse || d.useProactiveResponses)) || false;
            const hasInboundWarning = warnings.some(w => w.type === 'proactive-inbound');
            const hasNoInboundWarning = warnings.some(w => w.type === 'no-proactive-inbound');
            const hasOutboundWarning = warnings.some(w => w.type === 'proactive-outbound');
            const hasSpanishMismatch = warnings.some(w => w.type === 'spanish-mismatch');

            if (filters.proactive === 'disabled') {
                if (hasInboundProactive && !hasNoInboundWarning) return false;
            } else if (filters.proactive === 'anomalies') {
                if (!hasOutboundWarning && !hasSpanishMismatch && !hasNoInboundWarning && !hasInboundWarning) return false;
            } else if (filters.proactive === 'standard') {
                if (!hasInboundProactive || hasInboundWarning || hasNoInboundWarning) return false;
            } else if (filters.proactive === 'custom') {
                if (!hasInboundProactive || !hasInboundWarning || hasNoInboundWarning) return false;
            }
        }

        if (filters.voice) {
            const hasTelephony = status.rawDeployments && status.rawDeployments.length > 0;
            const hasVoiceWarning = warnings.some(w => w.type === 'voice');

            if (filters.voice === 'none') {
                if (hasTelephony) return false;
            } else if (filters.voice === 'mixed') {
                if (!hasVoiceWarning) return false;
            } else if (filters.voice === 'default') {
                const allDefault = hasTelephony && !hasVoiceWarning && status.rawDeployments.every(d => {
                    const vId = extractVoiceId(d.ttsOptions?.[0]?.ttsVoice || d.ttsVoice || '');
                    return voiceOptions.some(o => o.voice === vId);
                });
                if (!allDefault) return false;
            } else if (filters.voice === 'custom') {
                const hasCustom = hasTelephony && !hasVoiceWarning && status.rawDeployments.some(d => {
                    const vId = extractVoiceId(d.ttsOptions?.[0]?.ttsVoice || d.ttsVoice || '');
                    return !voiceOptions.some(o => o.voice === vId);
                });
                if (!hasCustom) return false;
            }
        }

        if (filters.model) {
            const appModel = getResolvedGenAIModel(app, status, sharedStatus);
            if (filters.model === 'none') {
                if (appModel) return false;
            } else {
                if (appModel !== filters.model) return false;
            }
        }

        const syncStr = status.hasModules ? formatDate(status.lastSync) : 'No module applied';
        if (filters.lastSync && !syncStr.toLowerCase().includes(filters.lastSync.toLowerCase())) return false;

        const pubStr = app.allowsTemplating ? 'N/A' : formatDate(status.lastPublish);
        if (filters.lastPublish && !pubStr.toLowerCase().includes(filters.lastPublish.toLowerCase())) return false;

        return true;
    });

    useEffect(() => {
        const load = async () => {
            if (organizationId) {
                try {
                    await variablesContainer.loadOrganizationVariablesByOrganizationId(organizationId);
                    setIsLoaded(true);
                } catch (err) {
                    console.error('Failed to load organization variables:', err);
                    setErrors(['Error loading organization constants.']);
                }
            }
        };
        load();
    }, [organizationId]);

    useEffect(() => {
        if (isLoaded && !hasInitialized && variablesContainer.organizationVariables) {
            const found = variablesContainer.organizationVariables.find(v => v.name === 'voicify_org_asr_context');
            if (found) {
                setAsrContext(found.value);
            }
            setHasInitialized(true);
        }
    }, [isLoaded, hasInitialized, variablesContainer.organizationVariables]);

    // Load and filter valid apps in the organization dynamically (all except manage menu apps)
    const loadApplications = async () => {
        if (!organizationId) return [];
        try {
            const result = await voicifyApi.getApplicationsForOrg(organizationId);
            const apps = result?.data || [];

            // 1. Identify which apps are manage menu apps (referenced or name heuristics)
            const manageMenuAppIds = new Set(
                apps.map(a => a.manageMenuApplicationId).filter(Boolean)
            );
            const isManageMenuApp = (a: ApplicationModel) =>
                manageMenuAppIds.has(a.id) ||
                (a?.name?.toLowerCase()?.startsWith("x -") && a?.name?.toLowerCase()?.endsWith("menu")) ||
                a?.name?.toLowerCase()?.includes("manage menu") ||
                a?.name?.toLowerCase()?.includes("manage entities");

            // 2. Filter apps (include location apps & allowsTemplating shared topics apps; exclude ONLY manage menu apps)
            const filtered = apps.filter(a => !isManageMenuApp(a));

            setResolvedLocationApps(filtered);
            return filtered;
        } catch (err) {
            console.error('Failed to retrieve applications:', err);
            setErrors(prev => [...prev, 'Failed to retrieve applications in this organization.']);
            return [];
        }
    };

    // Fetch and sync the module sync and publish timestamps for all resolved locations
    const loadLocationStatuses = async (appsToLoad = resolvedLocationApps) => {
        if (!appsToLoad || appsToLoad.length === 0) return;
        const statuses: Record<string, LocationStatus> = { ...locationStatuses };

        // Pre-initialize empty statuses for any new apps to load
        appsToLoad.forEach(app => {
            if (!statuses[app.id!]) {
                statuses[app.id!] = { lastSync: null, lastPublish: null, status: 'Idle', hasModules: true, todaysHours: null, transferTarget: null, timeZones: [], connections: [], rawDeployments: [], disabledDaysRaw: null, genAiModelRaw: null, isLoading: true };
            } else {
                statuses[app.id!] = { ...statuses[app.id!], isLoading: true };
            }
        });
        setLocationStatuses({ ...statuses });

        const concurrencyLimit = 5;
        let appQueue = [...appsToLoad];

        const worker = async () => {
            while (appQueue.length > 0) {
                const app = appQueue.shift();
                if (!app) break;

                const appId = app.id!;
                let lastSync: string | null = null;
                let lastPublish: string | null = null;
                let hasModules = true;
                let todaysHours: string | null = null;
                let transferTarget: string | null = null;
                let timeZones: string[] = [];
                let connections: ConnectionProfile[] = [];
                let rawDeployments: TelephonyDeploymentResponse[] = [];
                let disabledDaysRaw: string | null = null;
                let genAiModelRaw: string | null = null;
                let posConfigIssues: string[] = shouldValidatePosConfig(app.allowsTemplating, getAppType(app), organizationHasDentalApplication) ? ['POS config could not be loaded.'] : [];

                try {
                    // Check if there are any applied templates/modules
                    if (appliedTemplateContainer) {
                        const templatesResult = await appliedTemplateContainer.loadAppliedTemplates(appId);
                        hasModules = !!(templatesResult?.resultType === 'Ok' && templatesResult.data?.length > 0);

                        if (hasModules && templatesResult.data) {
                            const days = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
                            const todayDayName = days[new Date().getDay()];
                            const todayVariable = `{${todayDayName}}`;

                            for (const template of templatesResult.data) {
                                const allFieldValues = [
                                    ...(template.templateFormFieldValues || []),
                                    ...(template.dynamicFormFieldValuesCache || [])
                                ];
                                if (allFieldValues.length > 0) {
                                    for (const fieldValue of allFieldValues) {
                                        const assocVar = (fieldValue.associatedVariable || '').trim();
                                        const assocVarUpper = assocVar.toUpperCase();

                                        // 1. Match today's hours
                                        if (assocVarUpper === todayVariable || assocVarUpper === todayDayName) {
                                            todaysHours = fieldValue.value || null;
                                        }

                                        // 2. Match transfer target
                                        if (
                                            assocVar === '{TransferTarget}' ||
                                            assocVar === 'TransferTarget' ||
                                            assocVar === '{{session.returntostorenumber}}' ||
                                            assocVar === 'session.returntostorenumber'
                                        ) {
                                            transferTarget = fieldValue.value || null;
                                        }

                                        // 3. Match Time Zone ({TIME_ZONE} or {Webhook-Time-Zone})
                                        if (
                                            assocVarUpper === '{TIME_ZONE}' ||
                                            assocVarUpper === 'TIME_ZONE' ||
                                            assocVarUpper === '{WEBHOOK-TIME-ZONE}' ||
                                            assocVarUpper === 'WEBHOOK-TIME-ZONE'
                                        ) {
                                            if (fieldValue.value) {
                                                timeZones.push(fieldValue.value);
                                            }
                                        }

                                        // 4. Match DISABLED_DAYS
                                        if (assocVarUpper === 'DISABLED_DAYS' || assocVarUpper === '{DISABLED_DAYS}') {
                                            disabledDaysRaw = fieldValue.value || null;
                                        }

                                        // 5. Match genAIProvider
                                        if (assocVar === 'genAIProvider' || assocVar === '{genAIProvider}') {
                                            genAiModelRaw = fieldValue.value || null;
                                        }
                                    }
                                }
                            }
                        }
                    }

                    // 1. Fetch completed module sync history (only if modules exist)
                    if (hasModules) {
                        const syncsResult = await voicifyApi.getAppliedTemplateSyncsForApplication(appId);
                        if (syncsResult?.resultType === 'Ok' && syncsResult.data?.length > 0) {
                            const completedSyncs = syncsResult.data
                                .filter(s => s.stage === 'Complete' && s.createdDate)
                                .sort((a, b) => new Date(b.createdDate!).getTime() - new Date(a.createdDate!).getTime());
                            if (completedSyncs.length > 0) {
                                lastSync = completedSyncs[0].createdDate!;
                            }
                        }
                    }

                    // 2. Fetch completed publish history (applicable to all apps, including templated/shared topics apps)
                    const assistantAppResult = await voicifyApi.getAssistantApplication(appId);
                    if (assistantAppResult?.resultType === 'Ok' && assistantAppResult.data?.environments) {
                        const productionEnv = assistantAppResult.data.environments.find(e => e.name === 'Production');
                        if (productionEnv) {
                            const publishesResult = await voicifyApi.getEnvironmentPublishes(productionEnv.id, 0, 1);
                            if (publishesResult?.resultType === 'Ok' && publishesResult.data?.length > 0) {
                                lastPublish = publishesResult.data[0].createdDate || null;
                            }
                        }
                    }

                    // 3. Fetch telephony connection details (ASR, Proactive Response Prompts, and Voices)
                    if (!app.allowsTemplating) {
                        const telephonyResult = await voicifyApi.getTelephonyDeployments(appId);
                        if (telephonyResult?.resultType === 'Ok' && telephonyResult.data?.length > 0) {
                            rawDeployments = telephonyResult.data;
                            connections = telephonyResult.data.map(d => {
                                const provider = d.ttsOptions?.[0]?.ttsProvider || d.ttsProvider || 'N/A';
                                const voiceString = d.ttsOptions?.[0]?.ttsVoice || d.ttsVoice || '';
                                return {
                                    phoneNumber: d.phoneNumber || 'N/A',
                                    asrProvider: d.asrProvider || 'N/A',
                                    proactiveResponsePrompt: d.generativeProactiveResponsePrompt || 'None',
                                    ttsProvider: provider,
                                    voiceString,
                                    friendlyName: getVoiceFriendlyName(voiceString)
                                };
                            });
                        }

                        if (shouldValidatePosConfig(app.allowsTemplating, getAppType(app), organizationHasDentalApplication)) {
                            const posConfigResult = await voicifyApi.getPOSConfig(appId);
                            if (posConfigResult?.resultType === 'Ok') {
                                posConfigIssues = getPosConfigIssues(posConfigResult.data.data, appId, app.secret);
                                if (posConfigResult.data.posName?.trim().toUpperCase() === SPEEDLINE &&
                                    posConfigResult.data.hasWorldPayValues === false) {
                                    posConfigIssues.push('Worldpay credentials are missing.');
                                }
                            }
                        }
                    }
                    if (timeZones.length === 0 && variablesContainer.organizationVariables) {
                        const tzVar = variablesContainer.organizationVariables.find(v => {
                            const nameUpper = (v.name || '').trim().toUpperCase();
                            return nameUpper === '{TIME_ZONE}' ||
                                nameUpper === 'TIME_ZONE' ||
                                nameUpper === '{WEBHOOK-TIME-ZONE}' ||
                                nameUpper === 'WEBHOOK-TIME-ZONE';
                        });
                        if (tzVar && tzVar.value) {
                            timeZones.push(tzVar.value);
                        }
                    }

                    if (!disabledDaysRaw && variablesContainer.organizationVariables) {
                        const disabledDaysVar = variablesContainer.organizationVariables.find(v => {
                            const nameUpper = (v.name || '').trim().toUpperCase();
                            return nameUpper === 'DISABLED_DAYS' || nameUpper === '{DISABLED_DAYS}';
                        });
                        if (disabledDaysVar && disabledDaysVar.value) {
                            disabledDaysRaw = disabledDaysVar.value;
                        }
                    }

                    if (!genAiModelRaw && variablesContainer.organizationVariables) {
                        const genAiVar = variablesContainer.organizationVariables.find(v => {
                            const name = (v.name || '').trim();
                            return name === 'genAIProvider' || name === '{genAIProvider}';
                        });
                        if (genAiVar && genAiVar.value) {
                            genAiModelRaw = genAiVar.value;
                        }
                    }
                } catch (innerErr) {
                    console.error(`Failed to fetch sync/publish dates for ${app.name}:`, innerErr);
                }

                // Update state incrementally so we cascade load rows smoothly!
                if (isMounted.current) {
                    setLocationStatuses(prev => ({
                        ...prev,
                        [appId]: {
                            lastSync,
                            lastPublish,
                            status: prev[appId]?.status || 'Idle',
                            hasModules,
                            todaysHours,
                            transferTarget,
                            timeZones,
                            connections,
                            rawDeployments,
                            disabledDaysRaw,
                            genAiModelRaw,
                            posConfigIssues,
                            isLoading: false
                        }
                    }));
                }
            }
        };

        try {
            const workers = [];
            for (let i = 0; i < Math.min(concurrencyLimit, appsToLoad.length); i++) {
                workers.push(worker());
            }
            await Promise.all(workers);
        } catch (err) {
            console.error('Failed to load location statuses via concurrency queue:', err);
        }
    };

    // Load location list and statuses when panel is expanded
    useEffect(() => {
        if (!isPublishStatusCollapsed && organizationId) {
            const fetchAll = async () => {
                setIsLoadingStatus(true);
                const apps = await loadApplications();
                if (apps && apps.length > 0) {
                    await loadLocationStatuses(apps);
                }
                setIsLoadingStatus(false);
            };
            fetchAll();
        }
    }, [isPublishStatusCollapsed, organizationId]);

    useEffect(() => {
        if (resolvedLocationApps.length > 0) {
            const hasLoaded = Object.keys(locationStatuses).length > 0;
            if (!hasLoaded && !isLoadingStatus) {
                loadLocationStatuses(resolvedLocationApps);
            }
        }
    }, [resolvedLocationApps.length, organizationId, isLoadingStatus]);

    const handleSave = async () => {
        setErrors([]);
        setSuccesses([]);

        let valueToSave = asrContext;
        if (!valueToSave || valueToSave.trim() === '') {
            valueToSave = '{}';
        }

        const validationError = getSonioxContextValidationError(valueToSave, 20000);
        if (validationError) {
            setErrors([validationError]);
            return;
        }

        setIsSaving(true);
        try {
            const existing = variablesContainer.organizationVariables?.find(v => v.name === 'voicify_org_asr_context');
            let result;

            if (existing) {
                result = await variablesContainer.updateOrganizationVariable(existing.id!, {
                    ...existing,
                    value: valueToSave
                });
            } else {
                result = await variablesContainer.createOrganizationVariable({
                    name: 'voicify_org_asr_context',
                    value: valueToSave,
                    organizationId: organizationId
                });
            }

            if (result?.resultType === 'Ok') {
                setSuccesses(['ASR Custom Context saved successfully! Note: The application must be republished to pick up updates to this constant.']);
            } else if (result?.errors) {
                setErrors(result.errors);
            } else {
                setErrors(['Failed to save ASR Custom Context.']);
            }
        } catch (err) {
            console.error('Error saving organization variable:', err);
            setErrors(['An unexpected error occurred while saving.']);
        } finally {
            setIsSaving(false);
        }
    };

    // Sync or Sync & Publish a single location sequentially
    const handleSyncAndPublishLocation = async (app: ApplicationModel) => {
        setErrors([]);
        setSuccesses([]);

        const appStatus = locationStatuses[app.id!];
        const hasModules = appStatus?.hasModules ?? true;

        try {
            if (app.allowsTemplating) {
                // Shared topics: Sync only
                setLocationStatuses(prev => ({
                    ...prev,
                    [app.id!]: { ...prev[app.id!], status: 'Syncing...' }
                }));
                const syncResult = await syncApps([app], null, 1);
                if (syncResult.errorCount > 0) {
                    throw new Error(syncResult.errors?.[0] || 'Module sync failed.');
                }
                setLocationStatuses(prev => ({
                    ...prev,
                    [app.id!]: { ...prev[app.id!], status: 'Complete' }
                }));
                setSuccesses([`Successfully synced ${app.name}!`]);
            } else if (!hasModules) {
                // No modules: Publish only
                setLocationStatuses(prev => ({
                    ...prev,
                    [app.id!]: { ...prev[app.id!], status: 'Publishing...' }
                }));
                const publishResult = await publishApps([app], 1);
                if (publishResult.errorCount > 0) {
                    throw new Error(publishResult.errors?.[0] || 'Full publish failed.');
                }
                setLocationStatuses(prev => ({
                    ...prev,
                    [app.id!]: { ...prev[app.id!], status: 'Complete' }
                }));
                setSuccesses([`Successfully published ${app.name}!`]);
            } else {
                // Regular location: Sync & Publish
                setLocationStatuses(prev => ({
                    ...prev,
                    [app.id!]: { ...prev[app.id!], status: 'Syncing...' }
                }));
                const syncResult = await syncApps([app], commonApp, 1);
                if (syncResult.errorCount > 0) {
                    throw new Error(syncResult.errors?.[0] || 'Module sync failed.');
                }

                // Update status to Publishing
                setLocationStatuses(prev => ({
                    ...prev,
                    [app.id!]: { ...prev[app.id!], status: 'Publishing...' }
                }));

                // Publish templates
                const publishResult = await publishApps([app], 1);
                if (publishResult.errorCount > 0) {
                    throw new Error(publishResult.errors?.[0] || 'Template publish failed.');
                }

                setLocationStatuses(prev => ({
                    ...prev,
                    [app.id!]: { ...prev[app.id!], status: 'Complete' }
                }));
                setSuccesses([`Successfully synced and published ${app.name}!`]);
            }
            await loadLocationStatuses(resolvedLocationApps);
        } catch (err) {
            console.error(`Error during sync/publish for ${app.name}:`, err);
            setLocationStatuses(prev => ({
                ...prev,
                [app.id!]: { ...prev[app.id!], status: 'Failed' }
            }));
            const errMsg = (err as any).message || (err as any).toString();
            setErrors([`Sync failed for ${app.name}: ${errMsg.replace(/^Error:s*/, '')}`]);
        }
    };

    // Sync & Publish all locations sequentially
    const handleSyncAndPublishAll = async () => {
        if (!resolvedLocationApps || resolvedLocationApps.length === 0) return;
        setErrors([]);
        setSuccesses([]);

        const startingStatuses = { ...locationStatuses };
        resolvedLocationApps.forEach(app => {
            startingStatuses[app.id!] = { ...startingStatuses[app.id!], status: 'Syncing...' };
        });
        setLocationStatuses(startingStatuses);

        try {
            // Segregate templates, standard publishable locations, and publish-only empty locations
            const sharedTopicsApp = resolvedLocationApps.find(app => app.allowsTemplating);
            const syncAndPublishApps = resolvedLocationApps.filter(app => !app.allowsTemplating && locationStatuses[app.id!]?.hasModules);
            const publishOnlyApps = resolvedLocationApps.filter(app => !app.allowsTemplating && !locationStatuses[app.id!]?.hasModules);

            // 1. Sync standard locations (with modules) with a concurrency limit of 5 parallel workers
            if (syncAndPublishApps.length > 0) {
                const syncResult = await syncApps(syncAndPublishApps, sharedTopicsApp, 5);
                if (syncResult.errorCount > 0) {
                    throw new Error(`Bulk sync failed. ${syncResult.errors?.[0] || ''}`);
                }
            } else if (sharedTopicsApp) {
                // If there are no standard locations, we still need to sync the shared config.
                const syncResult = await syncApps([], sharedTopicsApp, 5);
                if (syncResult.errorCount > 0) {
                    throw new Error(`Bulk sync failed. ${syncResult.errors?.[0] || ''}`);
                }
            }

            const publishingStatuses = { ...locationStatuses };
            resolvedLocationApps.forEach(app => {
                if (app.allowsTemplating) {
                    publishingStatuses[app.id!] = { ...publishingStatuses[app.id!], status: 'Complete' };
                } else {
                    publishingStatuses[app.id!] = { ...publishingStatuses[app.id!], status: 'Publishing...' };
                }
            });
            setLocationStatuses(publishingStatuses);

            // 2. Publish standard locations AND empty-module locations together in parallel (limit 5)
            const allPublishApps = [...syncAndPublishApps, ...publishOnlyApps];
            if (allPublishApps.length > 0) {
                const publishResult = await publishApps(allPublishApps, 5);
                if (publishResult.errorCount > 0) {
                    throw new Error(`Bulk publish failed. ${publishResult.errors?.[0] || ''}`);
                }
            }

            const finalStatuses = { ...locationStatuses };
            resolvedLocationApps.forEach(app => {
                finalStatuses[app.id!] = { ...finalStatuses[app.id!], status: 'Complete' };
            });
            setLocationStatuses(finalStatuses);
            setSuccesses(['Successfully synced and published all locations!']);
            await loadLocationStatuses(resolvedLocationApps);
        } catch (err) {
            console.error('Error during bulk sync & publish:', err);
            const errorStatuses = { ...locationStatuses };
            resolvedLocationApps.forEach(app => {
                errorStatuses[app.id!] = { ...errorStatuses[app.id!], status: 'Failed' };
            });
            setLocationStatuses(errorStatuses);
            setErrors([`Bulk operation completed with errors: ${(err as any).message || (err as any).toString()}`]);
        }
    };

    // Sync & Publish only selected locations sequentially
    const handleSyncAndPublishSelected = async () => {
        const appsToPublish = resolvedLocationApps.filter(app => selectedAppIds.has(app.id!));
        if (!appsToPublish || appsToPublish.length === 0) return;
        setErrors([]);
        setSuccesses([]);

        const startingStatuses = { ...locationStatuses };
        appsToPublish.forEach(app => {
            startingStatuses[app.id!] = { ...startingStatuses[app.id!], status: 'Syncing...' };
        });
        setLocationStatuses(startingStatuses);

        try {
            // Segregate templates, standard publishable locations, and publish-only empty locations
            const sharedTopicsApp = appsToPublish.find(app => app.allowsTemplating);
            const syncAndPublishApps = appsToPublish.filter(app => !app.allowsTemplating && locationStatuses[app.id!]?.hasModules);
            const publishOnlyApps = appsToPublish.filter(app => !app.allowsTemplating && !locationStatuses[app.id!]?.hasModules);

            // 1. Sync selected standard locations (with modules) with a concurrency limit of 5 parallel workers
            if (syncAndPublishApps.length > 0) {
                const syncResult = await syncApps(syncAndPublishApps, sharedTopicsApp, 5);
                if (syncResult.errorCount > 0) {
                    throw new Error(`Bulk sync failed. ${syncResult.errors?.[0] || ''}`);
                }
            } else if (sharedTopicsApp) {
                // If only the shared config was selected, we still need to sync it.
                const syncResult = await syncApps([], sharedTopicsApp, 5);
                if (syncResult.errorCount > 0) {
                    throw new Error(`Bulk sync failed. ${syncResult.errors?.[0] || ''}`);
                }
            }

            const publishingStatuses = { ...locationStatuses };
            appsToPublish.forEach(app => {
                if (app.allowsTemplating) {
                    publishingStatuses[app.id!] = { ...publishingStatuses[app.id!], status: 'Complete' };
                } else {
                    publishingStatuses[app.id!] = { ...publishingStatuses[app.id!], status: 'Publishing...' };
                }
            });
            setLocationStatuses(publishingStatuses);

            // 2. Publish selected standard locations AND empty-module locations together in parallel (limit 5)
            const allPublishApps = [...syncAndPublishApps, ...publishOnlyApps];
            if (allPublishApps.length > 0) {
                const publishResult = await publishApps(allPublishApps, 5);
                if (publishResult.errorCount > 0) {
                    throw new Error(`Bulk publish failed. ${publishResult.errors?.[0] || ''}`);
                }
            }

            const finalStatuses = { ...locationStatuses };
            appsToPublish.forEach(app => {
                finalStatuses[app.id!] = { ...finalStatuses[app.id!], status: 'Complete' };
            });
            setLocationStatuses(finalStatuses);
            if (appsToPublish.every(app => app.allowsTemplating)) {
                setSuccesses([`Successfully synced ${appsToPublish.length} selected location${appsToPublish.length === 1 ? '' : 's'}!`]);
            } else {
                setSuccesses([`Successfully synced and published ${appsToPublish.length} selected location${appsToPublish.length === 1 ? '' : 's'}!`]);
            }
            setSelectedAppIds(new Set()); // Clear selection upon successful operation
            await loadLocationStatuses(resolvedLocationApps);
        } catch (err) {
            console.error('Error during selected bulk sync & publish:', err);
            const errorStatuses = { ...locationStatuses };
            appsToPublish.forEach(app => {
                errorStatuses[app.id!] = { ...errorStatuses[app.id!], status: 'Failed' };
            });
            setLocationStatuses(errorStatuses);
            setErrors([`Selected bulk operation completed with errors: ${(err as any).message || (err as any).toString()}`]);
        }
    };

    const renderSortIcon = (columnName: typeof sort.column) => {
        const isCurrent = sort.column === columnName;
        if (!isCurrent) {
            return (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#a0aec0" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle', marginLeft: '6px' }}>
                    <path d="M12 4L12 20M12 4L18 10M12 4L6 10" />
                    <path d="M12 20L18 14M12 20L6 14" />
                </svg>
            );
        }
        if (sort.direction === 'asc') {
            return (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#007dbb" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle', marginLeft: '6px' }}>
                    <line x1="12" y1="19" x2="12" y2="5"></line>
                    <polyline points="5 12 12 5 19 12"></polyline>
                </svg>
            );
        }
        return (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#007dbb" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle', marginLeft: '6px' }}>
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <polyline points="19 12 12 19 5 12"></polyline>
            </svg>
        );
    };

    return (
        <SettingsCard title="Management" bodyStyle={settingsCardBodyStyle}>
            <Container>
                {/* 1. Collapsible Other Organization Constants Card (renamed to Organization Constants Manager and first) */}
                <ContentCollapsingSection
                    title="Organization Constants Manager"
                    isCollapsedByDefault={true}
                    onToggle={(isCollapsed) => setIsOtherConstantsCollapsed(isCollapsed)}
                    className={collapsingSectionStyle}
                >
                    <OrganizationVariablesList organizationId={organizationId} />
                </ContentCollapsingSection>

                {/* 2. Collapsible Speech Recognition Context Card (renamed to Speech Recognition Context Manager and second) */}
                <ContentCollapsingSection
                    title="Speech Recognition Context Manager"
                    isCollapsedByDefault={true}
                    onToggle={(isCollapsed) => setIsAsrContextCollapsed(isCollapsed)}
                    className={collapsingSectionStyle}
                >
                    <SonioxContextEditor
                        name="voicify_org_asr_context"
                        title="Organization ASR"
                        description="Configure custom terms, vocabulary, or other hints that apply to all voice experiences in this organization."
                        value={asrContext}
                        onChange={(val) => setAsrContext(val)}
                        onBlur={() => { }}
                        onTouch={() => { }}
                        maxLength={20000}
                        showFieldError={false}
                    />

                    <SaveButtonWrapper>
                        <Button
                            themes={['primary']}
                            text={isSaving ? 'Saving...' : 'Save Context'}
                            onClick={handleSave}
                            disabled={isSaving}
                        />
                    </SaveButtonWrapper>
                </ContentCollapsingSection>

                {/* 3. Collapsible Location Status Card (renamed to Location Manager and third) */}
                <ContentCollapsingSection
                    title="Location Manager"
                    isCollapsedByDefault={false}
                    onToggle={(isCollapsed) => setIsPublishStatusCollapsed(isCollapsed)}
                    className={collapsingSectionStyle}
                    bottom={true}
                >
                    {isLoadingStatus ? (
                        <LocationManagerLoaderContainer>
                            <Loader />
                        </LocationManagerLoaderContainer>
                    ) : (
                        <>
                            {(() => {
                                const p1Warnings = visibleApps.flatMap(app => {
                                    const status = locationStatuses[app.id!] || { lastSync: null, lastPublish: null, status: 'Idle', hasModules: true, todaysHours: null, transferTarget: null, timeZones: [], connections: [], rawDeployments: [], disabledDaysRaw: null, genAiModelRaw: null, isLoading: true };
                                    const sharedApp = resolvedLocationApps.find(a => a.allowsTemplating);
                                    const sharedStatus = sharedApp ? locationStatuses[sharedApp.id!] : undefined;
                                    const warnings = deriveRowWarnings(app, status, sharedStatus, organizationHasDentalApplication);
                                    return warnings
                                        .filter(w => w.level === 'p1')
                                        .map(w => ({ app, warning: w }));
                                });

                                if (p1Warnings.length === 0) return null;

                                return (
                                    <ActionRequiredCard>
                                        <ActionRequiredHeader>
                                            <WarningIcon level="p1" style={{ marginRight: '6px', marginLeft: '0px' }} />
                                            <span>Action Required</span>
                                        </ActionRequiredHeader>
                                        <ActionRequiredList>
                                            {(isActionRequiredExpanded ? p1Warnings : p1Warnings.slice(0, 5)).map((item, idx) => (
                                                <ActionRequiredItem key={idx}>
                                                    <ActionRequiredLink
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            handleFilterChange('name', item.app.name);
                                                        }}
                                                    >
                                                        <strong>{item.app.name}:</strong>
                                                    </ActionRequiredLink>
                                                    {' '}{item.warning.message}
                                                </ActionRequiredItem>
                                            ))}
                                            {p1Warnings.length > 5 && (
                                                <ActionRequiredItem style={{ fontStyle: 'italic', color: '#3182ce', listStyleType: 'none', marginLeft: '-24px', marginTop: '8px', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setIsActionRequiredExpanded(prev => !prev)}>
                                                    {isActionRequiredExpanded ? 'Show less' : `+ ${p1Warnings.length - 5} more issues across your locations. Click to view all.`}
                                                </ActionRequiredItem>
                                            )}
                                        </ActionRequiredList>
                                    </ActionRequiredCard>
                                );
                            })()}
                            <ToolbarContainer>
                                <ToolbarLeft>
                                    <FilterInputWrapper style={{ width: '280px' }}>
                                        <SearchContainer>
                                            <ToolbarInput
                                                placeholder="Search & filter locations..."
                                                value={filters.name}
                                                onChange={(e) => handleFilterChange('name', e.target.value)}
                                                style={{ paddingRight: '36px' }}
                                            />
                                            {filters.name && (
                                                <ClearSearchButton onClick={() => handleFilterChange('name', '')} title="Clear search">
                                                    &times;
                                                </ClearSearchButton>
                                            )}
                                        </SearchContainer>
                                    </FilterInputWrapper>
                                </ToolbarLeft>

                                <ToolbarRight>
                                    {(filters.name || filters.warningState || filters.asr || filters.proactive || filters.voice || filters.isLive || filters.model) && (
                                        <ClearFiltersButton onClick={() => setFilters({ name: '', isLive: '', warningState: '', asr: '', proactive: '', voice: '', model: '', lastSync: '', lastPublish: '' })}>
                                            Clear Filters
                                        </ClearFiltersButton>
                                    )}
                                </ToolbarRight>
                            </ToolbarContainer>

                            {selectedAppIds.size > 0 && (
                                <BulkUpdatePanel>
                                    <BulkUpdateTitle>Bulk Update Selected ({selectedAppIds.size} Location{selectedAppIds.size !== 1 ? 's' : ''})</BulkUpdateTitle>
                                    <BulkUpdateGrid>
                                        {/* 1. Go-Live Status */}
                                        <BulkUpdateFieldCard>
                                            <span className="field-label">Go-Live Status</span>
                                            <div className="field-row">
                                                <FormDropdown
                                                    placeholder="Choose Status..."
                                                    value={bulkLiveValue}
                                                    onChange={(val) => {
                                                        setBulkLiveValue(val);
                                                        if (val !== 'draft') {
                                                            setBulkTargetLaunchDate('');
                                                        }
                                                    }}
                                                    options={[
                                                        { label: 'Live', value: 'live' },
                                                        { label: 'Draft', value: 'draft' }
                                                    ]}
                                                />
                                                <Button
                                                    themes={['secondary-small']}
                                                    loading={isBulkUpdating && bulkUpdatingField === 'live'}
                                                    text="Apply"
                                                    onClick={() => handleBulkUpdate('live', { status: bulkLiveValue, targetLaunchDate: bulkTargetLaunchDate })}
                                                    disabled={isBulkUpdating || !bulkLiveValue || (bulkLiveValue === 'draft' && bulkTargetLaunchDate.length > 0 && bulkTargetLaunchDate.length < 10)}
                                                />
                                            </div>
                                            {bulkLiveValue === 'draft' && (
                                                <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                    <label style={{ fontSize: '11px', color: '#777' }}>Target Launch Date (Optional)</label>
                                                    <div style={{ display: 'flex', alignItems: 'center', border: `1px solid ${silver_two}`, borderRadius: '4px', padding: '0px 8px', background: 'white' }}>
                                                        <DateInput
                                                            placeholder="MM/DD/YYYY"
                                                            value={bulkTargetLaunchDate}
                                                            onChange={(e) => {
                                                                let val = e.target.value.replace(/[^\d]/g, '');
                                                                if (val.length > 8) val = val.substring(0, 8);
                                                                if (val.length >= 5) {
                                                                    val = `${val.substring(0, 2)}/${val.substring(2, 4)}/${val.substring(4)}`;
                                                                } else if (val.length >= 3) {
                                                                    val = `${val.substring(0, 2)}/${val.substring(2)}`;
                                                                }
                                                                setBulkTargetLaunchDate(val);
                                                            }}
                                                            style={{ height: '34px', padding: 0 }}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </BulkUpdateFieldCard>

                                        {/* 2. Voice */}
                                        <BulkUpdateFieldCard>
                                            <span className="field-label">Voice Selection</span>
                                            <div className="field-row">
                                                <FormDropdown
                                                    placeholder="Choose Voice..."
                                                    value={voiceOptions.some(o => o.voice === bulkVoiceValue) ? bulkVoiceValue : (bulkVoiceValue ? 'custom' : '')}
                                                    onChange={(val) => {
                                                        if (val === 'custom') {
                                                            setBulkVoiceValue('custom_placeholder');
                                                        } else {
                                                            setBulkVoiceValue(val);
                                                        }
                                                    }}
                                                    options={[
                                                        ...voiceOptions.map(opt => ({ label: opt.label, value: opt.voice })),
                                                        { label: 'Custom Voice String', value: 'custom' }
                                                    ]}
                                                />
                                                <Button
                                                    themes={['secondary-small']}
                                                    loading={isBulkUpdating && bulkUpdatingField === 'voice'}
                                                    text="Apply"
                                                    onClick={() => {
                                                        if (!voiceOptions.some(o => o.voice === bulkVoiceValue)) {
                                                            handleBulkUpdate('voice', {
                                                                isCustom: true,
                                                                english: bulkVoiceEnglishValue,
                                                                spanish: bulkVoiceSpanishValue
                                                            });
                                                        } else {
                                                            handleBulkUpdate('voice', bulkVoiceValue);
                                                        }
                                                    }}
                                                    disabled={
                                                        isBulkUpdating ||
                                                        !bulkVoiceValue ||
                                                        (!voiceOptions.some(o => o.voice === bulkVoiceValue) && !bulkVoiceEnglishValue)
                                                    }
                                                />
                                            </div>
                                            {(!voiceOptions.some(o => o.voice === bulkVoiceValue) && bulkVoiceValue) && (
                                                <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                        <label style={{ fontSize: '11px', color: '#777' }}>English Voice String</label>
                                                        <textarea
                                                            rows={2}
                                                            placeholder="voice_id=...|model_id=..."
                                                            value={bulkVoiceEnglishValue}
                                                            onChange={(e) => setBulkVoiceEnglishValue(e.target.value)}
                                                            style={{
                                                                width: '100%',
                                                                padding: '6px 8px',
                                                                border: `1px solid ${silver_two}`,
                                                                borderRadius: '4px',
                                                                fontSize: '13px',
                                                                fontFamily: 'Muli',
                                                                resize: 'vertical'
                                                            }}
                                                        />
                                                    </div>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                        <label style={{ fontSize: '11px', color: '#777' }}>Spanish Voice String</label>
                                                        <textarea
                                                            rows={2}
                                                            placeholder="voice_id=...|model_id=..."
                                                            value={bulkVoiceSpanishValue}
                                                            onChange={(e) => setBulkVoiceSpanishValue(e.target.value)}
                                                            style={{
                                                                width: '100%',
                                                                padding: '6px 8px',
                                                                border: `1px solid ${silver_two}`,
                                                                borderRadius: '4px',
                                                                fontSize: '13px',
                                                                fontFamily: 'Muli',
                                                                resize: 'vertical'
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </BulkUpdateFieldCard>

                                        {/* 3. ASR Provider */}
                                        <BulkUpdateFieldCard>
                                            <span className="field-label">ASR Provider</span>
                                            <div className="field-row">
                                                <FormDropdown
                                                    placeholder="Choose ASR..."
                                                    value={bulkAsrValue}
                                                    onChange={(val) => setBulkAsrValue(val)}
                                                    options={[
                                                        { label: 'Deepgram', value: 'Deepgram' },
                                                        { label: 'Soniox', value: 'Soniox' },
                                                        { label: 'Google', value: 'Google' }
                                                    ]}
                                                />
                                                <Button
                                                    themes={['secondary-small']}
                                                    loading={isBulkUpdating && bulkUpdatingField === 'asr'}
                                                    text="Apply"
                                                    onClick={() => handleBulkUpdate('asr', bulkAsrValue)}
                                                    disabled={isBulkUpdating || !bulkAsrValue}
                                                />
                                            </div>
                                        </BulkUpdateFieldCard>

                                        <BulkUpdateFieldCard>
                                            <span className="field-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                Gen AI Model
                                                {(() => {
                                                    const selectedApps = resolvedLocationApps.filter(app => selectedAppIds.has(app.id!));
                                                    const hasHealthcare = selectedApps.some(app => getAppType(app) === 'Healthcare');
                                                    const hasOrdering = selectedApps.some(app => getAppType(app) === 'Ordering');

                                                    if (hasHealthcare) {
                                                        return (
                                                            <IconTooltip
                                                                text="This will update the Generative AI Provider module form field only. Models will still need to be updated inside content items as needed."
                                                                icon={infoIcon}
                                                                place="top"
                                                            />
                                                        );
                                                    } else if (hasOrdering) {
                                                        return (
                                                            <IconTooltip
                                                                text="This will update the Gen AI Model feature flag setting."
                                                                icon={infoIcon}
                                                                place="top"
                                                            />
                                                        );
                                                    }
                                                    return null;
                                                })()}
                                            </span>
                                            <div className="field-row">
                                                <FormDropdown
                                                    placeholder="Choose Model..."
                                                    value={bulkGenAIModel}
                                                    onChange={(val) => setBulkGenAIModel(val)}
                                                    options={GenAIModels.map(opt => ({ label: opt.label, value: opt.value }))}
                                                />
                                                <Button
                                                    themes={['secondary-small']}
                                                    loading={isBulkUpdating && bulkUpdatingField === 'genaiModel'}
                                                    text="Apply"
                                                    onClick={() => handleBulkUpdate('genaiModel', bulkGenAIModel)}
                                                    disabled={isBulkUpdating || !bulkGenAIModel}
                                                />
                                            </div>
                                        </BulkUpdateFieldCard>

                                        {/* 4. Proactive Response */}
                                        <BulkUpdateFieldCard>
                                            <span className="field-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                Proactive Response
                                                {(() => {
                                                    const selectedApps = resolvedLocationApps.filter(app => selectedAppIds.has(app.id!));
                                                    const hasHealthcare = selectedApps.some(app => getAppType(app) === 'Healthcare');
                                                    if (hasHealthcare) {
                                                        return (
                                                            <IconTooltip
                                                                text="Proactive response updates won't be applied to outbound."
                                                                icon={infoIcon}
                                                                place="top"
                                                            />
                                                        );
                                                    }
                                                    return null;
                                                })()}
                                            </span>
                                            <div className="field-row">
                                                <FormDropdown
                                                    placeholder="Choose Mode..."
                                                    value={bulkProactiveMode}
                                                    onChange={(val) => {
                                                        setBulkProactiveMode(val);
                                                        if (val === 'standard') {
                                                            setBulkProactiveValue('standard');
                                                        } else if (val === 'disable') {
                                                            setBulkProactiveValue('');
                                                        } else {
                                                            setBulkProactiveValue('');
                                                        }
                                                    }}
                                                    options={[
                                                        { label: 'Default Constants', value: 'standard' },
                                                        { label: 'Disable Proactive Responses', value: 'disable' },
                                                        { label: 'Custom Text Prompt', value: 'custom' }
                                                    ]}
                                                />
                                                <Button
                                                    themes={['secondary-small']}
                                                    loading={isBulkUpdating && bulkUpdatingField === 'proactive'}
                                                    text="Apply"
                                                    onClick={() => handleBulkUpdate('proactive', bulkProactiveValue)}
                                                    disabled={isBulkUpdating || !bulkProactiveMode}
                                                />
                                            </div>
                                            {bulkProactiveMode === 'custom' && (
                                                <ToolbarInput
                                                    placeholder="Enter custom proactive response prompt..."
                                                    value={bulkProactiveValue}
                                                    onChange={(e) => setBulkProactiveValue(e.target.value)}
                                                    style={{ width: '100%' }}
                                                />
                                            )}
                                        </BulkUpdateFieldCard>

                                        {/* 5. Closed Days */}
                                        <BulkUpdateFieldCard>
                                            <span className="field-label">Closed / Holiday Dates</span>
                                            <div className="field-row">
                                                <DateTagInputContainer>
                                                    {bulkClosedDaysDates.map((dateStr, i) => (
                                                        <DatePill key={i}>
                                                            {dateStr}
                                                            <button type="button" onClick={() => {
                                                                setBulkClosedDaysDates(prev => prev.filter((_, idx) => idx !== i));
                                                            }}>×</button>
                                                        </DatePill>
                                                    ))}
                                                    <DateInput
                                                        placeholder={bulkClosedDaysDates.length === 0 ? "MM/DD/YYYY" : ""}
                                                        value={bulkClosedDaysValue}
                                                        onChange={(e) => {
                                                            const inputVal = e.target.value;
                                                            if (inputVal.includes(',') || inputVal.includes(' ')) {
                                                                const parts = inputVal.split(/[, ]+/).map(p => p.trim()).filter(Boolean);
                                                                const validDates: string[] = [];
                                                                let remainder = '';

                                                                parts.forEach((p, idx) => {
                                                                    const cleanPart = p.replace(/[^\d]/g, '');
                                                                    if (cleanPart.length === 8) {
                                                                        const formatted = `${cleanPart.substring(0, 2)}/${cleanPart.substring(2, 4)}/${cleanPart.substring(4)}`;
                                                                        const match = formatted.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
                                                                        if (match && !bulkClosedDaysDates.includes(formatted) && !validDates.includes(formatted)) {
                                                                            validDates.push(formatted);
                                                                        }
                                                                    } else if (idx === parts.length - 1) {
                                                                        remainder = cleanPart;
                                                                    }
                                                                });

                                                                if (validDates.length > 0) {
                                                                    setBulkClosedDaysDates(prev => {
                                                                        const toAdd = validDates.filter(d => !prev.includes(d));
                                                                        return [...prev, ...toAdd];
                                                                    });
                                                                }

                                                                let val = remainder;
                                                                if (val.length > 8) val = val.substring(0, 8);
                                                                if (val.length >= 5) {
                                                                    val = `${val.substring(0, 2)}/${val.substring(2, 4)}/${val.substring(4)}`;
                                                                } else if (val.length >= 3) {
                                                                    val = `${val.substring(0, 2)}/${val.substring(2)}`;
                                                                }
                                                                setBulkClosedDaysValue(val);
                                                                return;
                                                            }

                                                            let val = inputVal.replace(/[^\d]/g, '');
                                                            if (val.length > 8) val = val.substring(0, 8);
                                                            if (val.length >= 5) {
                                                                val = `${val.substring(0, 2)}/${val.substring(2, 4)}/${val.substring(4)}`;
                                                            } else if (val.length >= 3) {
                                                                val = `${val.substring(0, 2)}/${val.substring(2)}`;
                                                            }
                                                            setBulkClosedDaysValue(val);
                                                        }}
                                                        onBlur={(e) => {
                                                            const val = e.target.value;
                                                            if (val.length === 10) {
                                                                const match = val.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
                                                                if (match && !bulkClosedDaysDates.includes(val)) {
                                                                    setBulkClosedDaysDates(prev => [...prev, val]);
                                                                    setBulkClosedDaysValue('');
                                                                }
                                                            }
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                e.preventDefault();
                                                                if (bulkClosedDaysValue.length === 10) {
                                                                    const match = bulkClosedDaysValue.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
                                                                    if (match && !bulkClosedDaysDates.includes(bulkClosedDaysValue)) {
                                                                        setBulkClosedDaysDates(prev => [...prev, bulkClosedDaysValue]);
                                                                        setBulkClosedDaysValue('');
                                                                    }
                                                                }
                                                            } else if (e.key === 'Backspace' && bulkClosedDaysValue === '' && bulkClosedDaysDates.length > 0) {
                                                                setBulkClosedDaysDates(prev => prev.slice(0, -1));
                                                            }
                                                        }}
                                                    />
                                                </DateTagInputContainer>
                                                <Button
                                                    themes={['secondary-small']}
                                                    loading={isBulkUpdating && bulkUpdatingField === 'closedDays'}
                                                    text="Apply"
                                                    onClick={() => {
                                                        let finalDates = [...bulkClosedDaysDates];
                                                        if (bulkClosedDaysValue.length === 10) {
                                                            const match = bulkClosedDaysValue.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
                                                            if (match && !finalDates.includes(bulkClosedDaysValue)) {
                                                                finalDates.push(bulkClosedDaysValue);
                                                            }
                                                        }
                                                        handleBulkUpdate('closedDays', finalDates);
                                                        setBulkClosedDaysDates([]);
                                                        setBulkClosedDaysValue('');
                                                    }}
                                                    disabled={isBulkUpdating || (bulkClosedDaysDates.length === 0 && bulkClosedDaysValue.length !== 10)}
                                                />
                                            </div>
                                        </BulkUpdateFieldCard>
                                    </BulkUpdateGrid>
                                </BulkUpdatePanel>
                            )}

                            {/* Minimalist plain text progress logs */}
                            {bulkLogs.length > 0 && (
                                <LogSection>
                                    <LogHeader>
                                        <span>Sync & Publish Progress Logs</span>
                                        {!isBulkProcessing && (
                                            <Button
                                                themes={['secondary-small']}
                                                text="Clear Logs"
                                                onClick={clearLogs}
                                            />
                                        )}
                                    </LogHeader>
                                    <LogArea>
                                        {bulkLogs.map((log, index) => (
                                            <LogLine key={index}>{log}</LogLine>
                                        ))}
                                    </LogArea>
                                </LogSection>
                            )}

                            {visibleApps.length === 0 ? (
                                <EmptyState>No active locations found matching your search and filter criteria.</EmptyState>
                            ) : (
                                <>
                                    {/* Desktop/Tablet Table view */}
                                    <TableContainer className="desktop-only">
                                        <Table>
                                            <thead>
                                                <tr>
                                                    <Th style={{ width: '40px', verticalAlign: 'middle' }}>
                                                        <CheckboxField
                                                            checked={visibleApps.length > 0 && visibleApps.every(app => selectedAppIds.has(app.id!))}
                                                            onChange={() => toggleSelectAll(visibleApps)}
                                                            className={customCheckboxStyle}
                                                        />
                                                    </Th>
                                                    <Th style={{ paddingLeft: '8px', verticalAlign: 'middle', width: '14%' }}>
                                                        <ThContent onClick={() => handleSort('name')}>
                                                            <span>Location</span>
                                                            {renderSortIcon('name')}
                                                        </ThContent>
                                                    </Th>
                                                    <Th style={{ verticalAlign: 'middle', width: '8%' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                            <DropdownFilter
                                                                title="Live Status"
                                                                value={filters.isLive}
                                                                onChange={(val) => handleFilterChange('isLive', val)}
                                                                isActive={!!filters.isLive}
                                                                options={[
                                                                    { label: 'Live Only', value: 'live' },
                                                                    { label: 'Draft Only', value: 'draft' },
                                                                    { label: 'Shared Configurations Only', value: 'shared' }
                                                                ]}
                                                            />
                                                            <SortButton onClick={() => handleSort('isLive')} title="Sort by live status">
                                                                {renderSortIcon('isLive')}
                                                            </SortButton>
                                                        </div>
                                                    </Th>
                                                    <Th style={{ verticalAlign: 'middle', width: '8%' }}>
                                                        <ThContent onClick={() => handleSort('lastSync')}>
                                                            <span>Last Sync</span>
                                                            {renderSortIcon('lastSync')}
                                                        </ThContent>
                                                    </Th>
                                                    <Th style={{ verticalAlign: 'middle', width: '8%' }}>
                                                        <ThContent onClick={() => handleSort('lastPublish')}>
                                                            <span>Last Publish</span>
                                                            {renderSortIcon('lastPublish')}
                                                        </ThContent>
                                                    </Th>
                                                    <Th style={{ verticalAlign: 'middle', width: '8%' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                            <DropdownFilter
                                                                title="ASR Provider"
                                                                value={filters.asr}
                                                                onChange={(val) => handleFilterChange('asr', val)}
                                                                isActive={!!filters.asr}
                                                                options={[
                                                                    { label: 'Deepgram Only', value: 'Deepgram' },
                                                                    { label: 'Soniox Only', value: 'Soniox' },
                                                                    { label: 'Google Only', value: 'Google' },
                                                                    { label: 'Mixed ASR', value: 'mixed' },
                                                                    { label: 'Not Configured', value: 'none' }
                                                                ]}
                                                            />
                                                            <SortButton onClick={() => handleSort('asr')} title="Sort by ASR Provider">
                                                                {renderSortIcon('asr')}
                                                            </SortButton>
                                                        </div>
                                                    </Th>
                                                    <Th style={{ verticalAlign: 'middle', width: '11%' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                            <DropdownFilter
                                                                title="Proactive Response"
                                                                value={filters.proactive}
                                                                onChange={(val) => handleFilterChange('proactive', val)}
                                                                isActive={!!filters.proactive}
                                                                options={[
                                                                    { label: 'Standard Only', value: 'standard' },
                                                                    { label: 'Custom Only', value: 'custom' },
                                                                    { label: 'Disabled Only', value: 'disabled' },
                                                                    { label: 'Anomalies', value: 'anomalies' }
                                                                ]}
                                                            />
                                                            <SortButton onClick={() => handleSort('proactive')} title="Sort by Proactive Response">
                                                                {renderSortIcon('proactive')}
                                                            </SortButton>
                                                        </div>
                                                    </Th>
                                                    <Th style={{ verticalAlign: 'middle', width: '8%' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                            <DropdownFilter
                                                                title="Voice"
                                                                value={filters.voice}
                                                                onChange={(val) => handleFilterChange('voice', val)}
                                                                isActive={!!filters.voice}
                                                                options={[
                                                                    { label: 'Default Only', value: 'default' },
                                                                    { label: 'Custom Only', value: 'custom' },
                                                                    { label: 'Mixed', value: 'mixed' },
                                                                    { label: 'Not Configured', value: 'none' }
                                                                ]}
                                                            />
                                                            <SortButton onClick={() => handleSort('voice')} title="Sort by Voice">
                                                                {renderSortIcon('voice')}
                                                            </SortButton>
                                                        </div>
                                                    </Th>
                                                    <Th style={{ verticalAlign: 'middle', width: '8%' }}>
                                                        <ThContent onClick={() => handleSort('hours')}>
                                                            <span>Hours Today</span>
                                                            {renderSortIcon('hours')}
                                                        </ThContent>
                                                    </Th>
                                                    <Th style={{ verticalAlign: 'middle', width: '11%' }}>
                                                        <span>This Year's Closed Days</span>
                                                    </Th>
                                                    <Th style={{ verticalAlign: 'middle', width: '8%' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                            <DropdownFilter
                                                                title="Gen AI"
                                                                value={filters.model}
                                                                onChange={(val) => handleFilterChange('model', val)}
                                                                isActive={!!filters.model}
                                                                options={[
                                                                    ...GenAIModels,
                                                                    { label: 'Not Configured', value: 'none' }
                                                                ]}
                                                            />
                                                            <SortButton onClick={() => handleSort('model')} title="Sort by Gen AI Model">
                                                                {renderSortIcon('model')}
                                                            </SortButton>
                                                        </div>
                                                    </Th>
                                                    <Th style={{ verticalAlign: 'middle', width: '8%' }}>
                                                        <ThContent onClick={() => handleSort('target')}>
                                                            <span>Transfer Number</span>
                                                            {renderSortIcon('target')}
                                                        </ThContent>
                                                    </Th>
                                                    {!organizationHasDentalApplication && (
                                                        <Th style={{ verticalAlign: 'middle', width: '10%' }}>
                                                            <span>POS Config</span>
                                                        </Th>
                                                    )}
                                                    <Th style={{ textAlign: 'right', verticalAlign: 'middle', width: '10%' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>
                                                            <div style={{ width: '135px' }}>
                                                                <DropdownFilter
                                                                    title="Warning Summary"
                                                                    value={filters.warningState}
                                                                    onChange={(val) => handleFilterChange('warningState', val)}
                                                                    isActive={!!filters.warningState}
                                                                    alignRight={true}
                                                                    options={[
                                                                        { label: 'With Warnings', value: 'warnings' },
                                                                        { label: 'No Warnings', value: 'no-warnings' }
                                                                    ]}
                                                                />
                                                            </div>
                                                            <SortButton onClick={() => handleSort('warnings')} title="Sort by warning summary">
                                                                {renderSortIcon('warnings')}
                                                            </SortButton>
                                                        </div>
                                                    </Th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {visibleApps.map(app => {
                                                    const status = locationStatuses[app.id!] || { lastSync: null, lastPublish: null, status: 'Idle', hasModules: true, todaysHours: null, transferTarget: null, timeZones: [], connections: [], rawDeployments: [], isLoading: true };
                                                    const sharedApp = resolvedLocationApps.find(a => a.allowsTemplating);
                                                    const sharedStatus = sharedApp ? locationStatuses[sharedApp.id!] : undefined;
                                                    const rowWarnings = deriveRowWarnings(app, status, sharedStatus, organizationHasDentalApplication);
                                                    const typeOfApp = getAppType(app);

                                                    // Count telephony lines by outbound vs inbound
                                                    const inboundCount = (status.rawDeployments || []).filter(d => !d.useForOutboundCalling).length;
                                                    const outboundCount = (status.rawDeployments || []).filter(d => d.useForOutboundCalling).length;

                                                    return (
                                                        <tr key={app.id}>
                                                            <Td style={{ width: '40px' }}>
                                                                <CheckboxField
                                                                    checked={selectedAppIds.has(app.id!)}
                                                                    onChange={() => toggleSelectApp(app.id!)}
                                                                    className={customCheckboxStyle}
                                                                />
                                                            </Td>
                                                            <Td style={{ paddingLeft: '8px' }}>
                                                                <CellRow style={{ fontWeight: 700 }}>
                                                                    <LocationLink
                                                                        href={`${getBaseUrl()}/v/apps/${app.id}`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                    >
                                                                        {app.name}
                                                                    </LocationLink>
                                                                </CellRow>
                                                            </Td>
                                                            <Td style={{ verticalAlign: 'middle' }}>
                                                                {app.allowsTemplating ? (
                                                                    <span style={{ color: '#4a5568', fontSize: '13px' }}>N/A</span>
                                                                ) : (
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                        <TooltipWrapper
                                                                            text={app.isLive ? (app.launchDate ? `Launched: ${formatDateOnly(app.launchDate)}` : 'No launch date set') : (app.targetLaunchDate ? `Target Launch: ${formatDateOnly(app.targetLaunchDate)}` : 'No launch date set')}
                                                                            place="top"
                                                                        >
                                                                            <InlineLivePill isLive={!!app.isLive}>
                                                                                {app.isLive ? 'Live' : 'Draft'}
                                                                            </InlineLivePill>
                                                                        </TooltipWrapper>
                                                                        {(() => {
                                                                            const launchWarning = rowWarnings.find(w => w.type === 'launch-missed');
                                                                            if (!launchWarning) return null;
                                                                            return (
                                                                                <TooltipWrapper text={launchWarning.message} place="top" maxWidth={240}>
                                                                                    <WarningIcon level={launchWarning.level} />
                                                                                </TooltipWrapper>
                                                                            );
                                                                        })()}
                                                                    </div>
                                                                )}
                                                            </Td>
                                                            <Td style={{ verticalAlign: 'middle' }}>
                                                                <CellRow style={{ display: 'flex', alignItems: 'center', gap: '6px', width: '100%', minWidth: 0 }}>
                                                                    {status.isLoading ? (
                                                                        <CellSpinner />
                                                                    ) : status.hasModules ? (
                                                                        status.lastSync ? (
                                                                            <TooltipWrapper text={formatDate(status.lastSync)} place="top" maxWidth={240}>
                                                                                <span style={{ cursor: 'help', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatDateShort(status.lastSync)}</span>
                                                                            </TooltipWrapper>
                                                                        ) : (
                                                                            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Never synced</span>
                                                                        )
                                                                    ) : (
                                                                        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>No module</span>
                                                                    )}
                                                                    {!status.isLoading && (() => {
                                                                        const syncWarning = rowWarnings.find(w => w.type === 'sync');
                                                                        if (!syncWarning) return null;
                                                                        return (
                                                                            <TooltipWrapper text={syncWarning.message} place="top" maxWidth={240}>
                                                                                <WarningIcon level={syncWarning.level} />
                                                                            </TooltipWrapper>
                                                                        );
                                                                    })()}
                                                                </CellRow>
                                                            </Td>
                                                            <Td style={{ verticalAlign: 'middle' }}>
                                                                <CellRow style={{ display: 'flex', alignItems: 'center', gap: '6px', width: '100%', minWidth: 0 }}>
                                                                    {status.isLoading ? (
                                                                        <CellSpinner />
                                                                    ) : status.lastPublish ? (
                                                                        <TooltipWrapper text={formatDate(status.lastPublish)} place="top" maxWidth={240}>
                                                                            <span style={{ cursor: 'help', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatDateShort(status.lastPublish)}</span>
                                                                        </TooltipWrapper>
                                                                    ) : (
                                                                        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Never published</span>
                                                                    )}
                                                                    {!status.isLoading && (() => {
                                                                        const pubWarning = rowWarnings.find(w => w.type === 'publish');
                                                                        if (!pubWarning) return null;
                                                                        return (
                                                                            <TooltipWrapper text={pubWarning.message} place="top" maxWidth={240}>
                                                                                <WarningIcon level={pubWarning.level} />
                                                                            </TooltipWrapper>
                                                                        );
                                                                    })()}
                                                                </CellRow>
                                                            </Td>
                                                            <Td
                                                                style={{ verticalAlign: 'middle', cursor: 'pointer' }}
                                                                onClick={() => setActiveDetailAppId(app.id!)}
                                                            >
                                                                {app.allowsTemplating ? (
                                                                    <span style={{ color: '#4a5568', fontSize: '13px' }}>N/A</span>
                                                                ) : status.isLoading ? (
                                                                    <CellSpinner />
                                                                ) : (!status.rawDeployments || status.rawDeployments.length === 0 ? (
                                                                    <span style={{ color: '#4a5568', fontSize: '13px', fontStyle: 'normal' }}>Not Configured</span>
                                                                ) : (() => {
                                                                    const uniqueAsr = Array.from(new Set(status.rawDeployments.map(d => d.asrProvider || 'Deepgram')));
                                                                    if (uniqueAsr.length === 1) {
                                                                        return <span style={{ color: '#4a5568' }}>{uniqueAsr[0]}</span>;
                                                                    }
                                                                    return (
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                            <span style={{ color: '#4a5568' }}>Mixed</span>
                                                                            <TooltipWrapper text="ASR providers/hints differ across telephony lines." place="top" maxWidth={240}>
                                                                                <WarningIcon level="p2" />
                                                                            </TooltipWrapper>
                                                                        </div>
                                                                    );
                                                                })())}
                                                            </Td>
                                                            <Td
                                                                style={{ verticalAlign: 'middle', cursor: 'pointer' }}
                                                                onClick={() => setActiveDetailAppId(app.id!)}
                                                            >
                                                                {app.allowsTemplating ? (
                                                                    <span style={{ color: '#4a5568', fontSize: '13px' }}>N/A</span>
                                                                ) : status.isLoading ? (
                                                                    <CellSpinner />
                                                                ) : (!status.rawDeployments || status.rawDeployments.length === 0 ? (
                                                                    <span style={{ color: '#4a5568', fontSize: '13px', fontStyle: 'normal' }}>Not Configured</span>
                                                                ) : (() => {
                                                                    const outboundWarning = rowWarnings.find(w => w.type === 'proactive-outbound');
                                                                    const spanishMismatch = rowWarnings.find(w => w.type === 'spanish-mismatch');
                                                                    const inboundWarning = rowWarnings.find(w => w.type === 'proactive-inbound');
                                                                    const noInboundWarning = rowWarnings.find(w => w.type === 'no-proactive-inbound');
                                                                    const hasAnyProactive = status.rawDeployments.some(d => d.useGenerativeProactiveResponse || d.useProactiveResponses);

                                                                    if (noInboundWarning) {
                                                                        return (
                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                                <span style={{ color: '#4a5568' }}>Missing</span>
                                                                                <TooltipWrapper text={noInboundWarning.message} place="top" maxWidth={240}>
                                                                                    <WarningIcon level={noInboundWarning.level} />
                                                                                </TooltipWrapper>
                                                                            </div>
                                                                        );
                                                                    }
                                                                    if (outboundWarning || spanishMismatch) {
                                                                        const telWarnings = rowWarnings.filter(w => w.type === 'proactive-outbound' || w.type === 'spanish-mismatch');
                                                                        const highestLevel = telWarnings.some(w => w.level === 'p1') ? 'p1' : 'p2';
                                                                        return (
                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                                <span style={{ color: '#4a5568' }}>Mixed</span>
                                                                                <TooltipWrapper text={telWarnings.map(w => w.message).join('\n')} place="top" maxWidth={240}>
                                                                                    <WarningIcon level={highestLevel} />
                                                                                </TooltipWrapper>
                                                                            </div>
                                                                        );
                                                                    }
                                                                    if (inboundWarning) {
                                                                        return (
                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                                <span style={{ color: '#4a5568' }}>Custom</span>
                                                                                <TooltipWrapper text={inboundWarning.message} place="top" maxWidth={240}>
                                                                                    <WarningIcon level={inboundWarning.level} />
                                                                                </TooltipWrapper>
                                                                            </div>
                                                                        );
                                                                    }
                                                                    if (hasAnyProactive) {
                                                                        return <span style={{ color: '#4a5568' }}>Standard</span>;
                                                                    }
                                                                    return <span style={{ color: '#4a5568' }}>Disabled</span>;
                                                                })())}
                                                            </Td>
                                                            <Td
                                                                style={{ verticalAlign: 'middle', cursor: 'pointer' }}
                                                                onClick={() => setActiveDetailAppId(app.id!)}
                                                            >
                                                                {app.allowsTemplating ? (
                                                                    <span style={{ color: '#4a5568', fontSize: '13px' }}>N/A</span>
                                                                ) : status.isLoading ? (
                                                                    <CellSpinner />
                                                                ) : (!status.rawDeployments || status.rawDeployments.length === 0 ? (
                                                                    <span style={{ color: '#4a5568', fontSize: '13px', fontStyle: 'normal' }}>Not Configured</span>
                                                                ) : (() => {
                                                                    const voiceWarning = rowWarnings.find(w => w.type === 'voice');
                                                                    if (voiceWarning) {
                                                                        return (
                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                                <span style={{ color: '#4a5568' }}>Mixed</span>
                                                                                <TooltipWrapper text={voiceWarning.message} place="top" maxWidth={240}>
                                                                                    <WarningIcon level={voiceWarning.level} />
                                                                                </TooltipWrapper>
                                                                            </div>
                                                                        );
                                                                    }
                                                                    const voices = status.rawDeployments.map(d => extractVoiceId(d.ttsOptions?.[0]?.ttsVoice || d.ttsVoice || ''))
                                                                        .filter(Boolean);
                                                                    const uniqueVoices = Array.from(new Set(voices));
                                                                    if (uniqueVoices.length === 1) {
                                                                        const friendly = getVoiceFriendlyName(uniqueVoices[0]);
                                                                        const isCustom = friendly === uniqueVoices[0] || uniqueVoices[0].includes('voice_id=');
                                                                        if (isCustom) {
                                                                            return <span style={{ color: '#4a5568' }}>Custom</span>;
                                                                        }
                                                                        return <span style={{ color: '#4a5568' }}>{friendly}</span>;
                                                                    }
                                                                    return <span style={{ color: '#4a5568', fontSize: '13px', fontStyle: 'normal' }}>Not Configured</span>;
                                                                })())}
                                                            </Td>
                                                            <Td style={{ verticalAlign: 'middle' }}>
                                                                {app.allowsTemplating ? (
                                                                    <span style={{ color: '#4a5568', fontSize: '13px' }}>N/A</span>
                                                                ) : status.isLoading ? (
                                                                    <CellSpinner />
                                                                ) : (
                                                                    (() => {
                                                                        let tz = status.timeZones;
                                                                        if (!tz || tz.length === 0) {
                                                                            const sharedApp = resolvedLocationApps.find(a => a.allowsTemplating);
                                                                            if (sharedApp) {
                                                                                const sharedStatus = locationStatuses[sharedApp.id!];
                                                                                if (sharedStatus && sharedStatus.timeZones && sharedStatus.timeZones.length > 0) {
                                                                                    tz = sharedStatus.timeZones;
                                                                                }
                                                                            }
                                                                        }
                                                                        if ((!tz || tz.length === 0) && variablesContainer.organizationVariables) {
                                                                            const tzVar = variablesContainer.organizationVariables.find(v => {
                                                                                const nameUpper = (v.name || '').trim().toUpperCase();
                                                                                return nameUpper === '{TIME_ZONE}' ||
                                                                                    nameUpper === 'TIME_ZONE' ||
                                                                                    nameUpper === '{WEBHOOK-TIME-ZONE}' ||
                                                                                    nameUpper === 'WEBHOOK-TIME-ZONE';
                                                                            });
                                                                            if (tzVar && tzVar.value) {
                                                                                tz = [tzVar.value];
                                                                            }
                                                                        }
                                                                        const hoursVal = formatHoursToday(status.todaysHours, tz);
                                                                        return (
                                                                            <TooltipWrapper text={hoursVal} place="top" maxWidth={240}>
                                                                                <span style={{ cursor: 'help' }}>{hoursVal}</span>
                                                                            </TooltipWrapper>
                                                                        );
                                                                    })()
                                                                )}
                                                            </Td>
                                                            <Td style={{ verticalAlign: 'middle' }}>
                                                                {app.allowsTemplating ? (
                                                                    <span style={{ color: '#4a5568', fontSize: '13px' }}>N/A</span>
                                                                ) : status.isLoading ? (
                                                                    <CellSpinner />
                                                                ) : (() => {
                                                                    const sharedAppForFallback = resolvedLocationApps.find(a => a.allowsTemplating);
                                                                    const sharedStatusForFallback = sharedAppForFallback ? locationStatuses[sharedAppForFallback.id!] : undefined;
                                                                    const closedDays = getClosedDaysThisYear(status.disabledDaysRaw || (sharedStatusForFallback ? sharedStatusForFallback.disabledDaysRaw || '' : ''));
                                                                    if (closedDays.length === 0) {
                                                                        return (
                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                                <span style={{ color: '#4a5568' }}>None</span>
                                                                                <TooltipWrapper text="No closed days configured for this year." place="top" maxWidth={240}>
                                                                                    <WarningIcon level="p2" />
                                                                                </TooltipWrapper>
                                                                            </div>
                                                                        );
                                                                    }
                                                                    return (
                                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                                            {closedDays.map((day, idx) => (
                                                                                <ClosedDayPill key={idx}>{day}</ClosedDayPill>
                                                                            ))}
                                                                        </div>
                                                                    );
                                                                })()}
                                                            </Td>
                                                            <Td style={{ verticalAlign: 'middle' }}>
                                                                {app.allowsTemplating ? (
                                                                    <span style={{ color: '#4a5568', fontSize: '13px' }}>N/A</span>
                                                                ) : status.isLoading ? (
                                                                    <CellSpinner />
                                                                ) : (
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                        <EditableValueInDrawer
                                                                            title={`Click to edit Gen AI settings for ${app.name}`}
                                                                            onClick={() => startEditGenAIModel(app.id!, app)}
                                                                        >
                                                                            {getResolvedGenAIModel(app, status, sharedStatus) || 'Not Configured'}
                                                                        </EditableValueInDrawer>
                                                                        {(() => {
                                                                            const genaiWarning = rowWarnings.find(w => w.type === 'genai-prompt-mismatch');
                                                                            if (!genaiWarning) return null;
                                                                            return (
                                                                                <TooltipWrapper text={genaiWarning.message} place="top" maxWidth={240}>
                                                                                    <WarningIcon level={genaiWarning.level} />
                                                                                </TooltipWrapper>
                                                                            );
                                                                        })()}
                                                                    </div>
                                                                )}
                                                            </Td>
                                                            <Td style={{ verticalAlign: 'middle' }}>
                                                                {app.allowsTemplating ? (
                                                                    <span style={{ color: '#4a5568', fontSize: '13px' }}>N/A</span>
                                                                ) : status.isLoading ? (
                                                                    <CellSpinner />
                                                                ) : (
                                                                    (() => {
                                                                        const transferWarning = rowWarnings.find(w => w.type === 'dummy-transfer' || w.type === 'no-transfer');
                                                                        const formattedNumber = formatTransferNumber(status.transferTarget) || 'None';
                                                                        const displayTarget = status.transferTarget || '';
                                                                        const digitsOnly = displayTarget.replace(/\D/g, '');
                                                                        const isPhone = digitsOnly.length === 10 || (digitsOnly.length === 11 && digitsOnly.startsWith('1'));

                                                                        const renderInner = () => {
                                                                            if (isPhone) {
                                                                                return (
                                                                                    <TooltipWrapper text={formattedNumber} place="top" maxWidth={240}>
                                                                                        <span style={{ cursor: 'help' }}>...{digitsOnly.substring(digitsOnly.length - 4)}</span>
                                                                                    </TooltipWrapper>
                                                                                );
                                                                            }
                                                                            return <span style={{ display: 'inline-block', width: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={formattedNumber}>{formattedNumber}</span>;
                                                                        };

                                                                        if (transferWarning) {
                                                                            return (
                                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', width: '100%', minWidth: 0 }}>
                                                                                    <div style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                                        {renderInner()}
                                                                                    </div>
                                                                                    <TooltipWrapper text={transferWarning.message} place="top" maxWidth={240}>
                                                                                        <WarningIcon level={transferWarning.level} />
                                                                                    </TooltipWrapper>
                                                                                </div>
                                                                            );
                                                                        }
                                                                        return renderInner();
                                                                    })()
                                                                )}
                                                            </Td>
                                                            {!organizationHasDentalApplication && (
                                                                <Td style={{ verticalAlign: 'middle', whiteSpace: 'normal' }}>
                                                                    {app.allowsTemplating ? (
                                                                        <span style={{ color: '#4a5568', fontSize: '13px' }}>N/A</span>
                                                                    ) : status.isLoading ? (
                                                                        <CellSpinner />
                                                                    ) : (status.posConfigIssues || []).length === 0 ? (
                                                                        <span style={{ color: '#238636', fontSize: '13px', fontWeight: 600 }}>Valid</span>
                                                                    ) : (
                                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                                            {(status.posConfigIssues || []).map((issue, index) => (
                                                                                <TooltipWrapper key={index} text={issue} place={'top'} maxWidth={320}>
                                                                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', width: '100%', minWidth: 0, cursor: 'help' }}>
                                                                                        <WarningIcon level={'p1'} style={{ flexShrink: 0 }} />
                                                                                        <span style={{ fontSize: '12px', lineHeight: 1.3, whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{issue}</span>
                                                                                    </div>
                                                                                </TooltipWrapper>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </Td>
                                                            )}
                                                            <Td
                                                                style={{ textAlign: 'right', verticalAlign: 'middle', cursor: 'pointer' }}
                                                                onClick={() => setActiveDetailAppId(app.id!)}
                                                            >
                                                                {status.isLoading ? (
                                                                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}><CellSpinner /></div>
                                                                ) : rowWarnings.length > 0 ? (
                                                                    <WarningPillsContainer>
                                                                        {rowWarnings.map((w, index) => (
                                                                            <TooltipWrapper key={index} text={w.message} place="top" maxWidth={240}>
                                                                                <WarningPill type={w.type} level={w.level}>
                                                                                    {w.type === 'sync' ? 'Sync' :
                                                                                        w.type === 'publish' ? 'Publish' :
                                                                                            w.type === 'voice' ? 'Voice' :
                                                                                                w.type === 'proactive-outbound' ? 'Outbound Proactive' :
                                                                                                    w.type === 'spanish-mismatch' ? 'Spanish Mismatch' :
                                                                                                        w.type === 'no-proactive-inbound' ? 'No Inbound Proactive' :
                                                                                                            w.type === 'dummy-transfer' ? 'Dummy Transfer' :
                                                                                                                w.type === 'no-transfer' ? 'No Transfer' :
                                                                                                                    w.type === 'proactive-inbound' ? 'Inbound Proactive' :
                                                                                                                        w.type === 'no-closed-days' ? 'No Closed Days' :
                                                                                                                            w.type === 'asr' ? 'ASR' :
                                                                                                                                w.type === 'genai-prompt-mismatch' ? 'GenAI Prompt' :
                                                                                                                                    w.type === 'pos-config' ? 'POS Config' :
                                                                                                                                        'Launch Missed'}
                                                                                </WarningPill>
                                                                            </TooltipWrapper>
                                                                        ))}
                                                                    </WarningPillsContainer>
                                                                ) : (
                                                                    <span style={{ fontSize: '13px', color: '#2d3748' }}>No warnings</span>
                                                                )}
                                                            </Td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    </TableContainer>

                                    {/* Mobile/Tablet Card view */}
                                    <CardsGrid className="mobile-only">
                                        {visibleApps.map(app => {
                                            const status = locationStatuses[app.id!] || { lastSync: null, lastPublish: null, status: 'Idle', hasModules: true, todaysHours: null, transferTarget: null, timeZones: [], connections: [], rawDeployments: [], isLoading: true };
                                            const sharedApp = resolvedLocationApps.find(a => a.allowsTemplating);
                                            const sharedStatus = sharedApp ? locationStatuses[sharedApp.id!] : undefined;
                                            const rowWarnings = deriveRowWarnings(app, status, sharedStatus, organizationHasDentalApplication);
                                            const typeOfApp = getAppType(app);
                                            const inboundCount = (status.rawDeployments || []).filter(d => !d.useForOutboundCalling).length;
                                            const outboundCount = (status.rawDeployments || []).filter(d => d.useForOutboundCalling).length;

                                            return (
                                                <MobileLocationCard key={app.id} onClick={() => setActiveDetailAppId(app.id!)}>
                                                    <CardTopRow>
                                                        <CardCheckboxWrapper onClick={(e) => e.stopPropagation()}>
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedAppIds.has(app.id!)}
                                                                onChange={() => toggleSelectApp(app.id!)}
                                                            />
                                                        </CardCheckboxWrapper>
                                                        <CardTitleLink
                                                            href={`${getBaseUrl()}/v/apps/${app.id}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            {app.name}
                                                        </CardTitleLink>
                                                        {app.allowsTemplating ? (
                                                            <span style={{ color: '#2d3748', fontSize: '11px', fontWeight: 700 }}>N/A</span>
                                                        ) : (
                                                            <TooltipWrapper
                                                                text={app.isLive ? (app.launchDate ? `Launched: ${formatDateOnly(app.launchDate)}` : 'No launch date set') : (app.targetLaunchDate ? `Target Launch: ${formatDateOnly(app.targetLaunchDate)}` : 'No launch date set')}
                                                                place="top"
                                                            >
                                                                <InlineLivePill isLive={!!app.isLive}>
                                                                    {app.isLive ? 'Live' : 'Draft'}
                                                                </InlineLivePill>
                                                            </TooltipWrapper>
                                                        )}
                                                    </CardTopRow>

                                                    <CardBodyRow>
                                                        {rowWarnings.length > 0 ? (
                                                            <WarningPillsContainer style={{ justifyContent: 'flex-start' }}>
                                                                {rowWarnings.map((w, index) => (
                                                                    <WarningPill key={index} type={w.type} level={w.level}>
                                                                        {w.type === 'sync' ? 'Sync' :
                                                                            w.type === 'publish' ? 'Publish' :
                                                                                w.type === 'voice' ? 'Voice' :
                                                                                    w.type === 'proactive-outbound' ? 'Outbound Proactive' :
                                                                                        w.type === 'spanish-mismatch' ? 'Spanish Mismatch' :
                                                                                            w.type === 'no-proactive-inbound' ? 'No Inbound Proactive' :
                                                                                                w.type === 'dummy-transfer' ? 'Dummy Transfer' :
                                                                                                    w.type === 'no-transfer' ? 'No Transfer' :
                                                                                                        w.type === 'proactive-inbound' ? 'Inbound Proactive' :
                                                                                                            w.type === 'no-closed-days' ? 'No Closed Days' :
                                                                                                                w.type === 'asr' ? 'ASR' :
                                                                                                                    w.type === 'pos-config' ? 'POS Config' :
                                                                                                                        'Launch Missed'}
                                                                    </WarningPill>
                                                                ))}
                                                            </WarningPillsContainer>
                                                        ) : (
                                                            <span style={{ fontSize: '11px', color: '#2d3748' }}>No warnings</span>
                                                        )}
                                                    </CardBodyRow>

                                                    <CardInfoGrid>
                                                        <div>
                                                            <span className="label">Sync:</span>
                                                            <span className="value display-flex-align-center">
                                                                {status.hasModules ? (
                                                                    status.lastSync ? (
                                                                        <TooltipWrapper text={formatDate(status.lastSync)} place="top" maxWidth={240}>
                                                                            <span style={{ cursor: 'help' }}>{formatDateShort(status.lastSync)}</span>
                                                                        </TooltipWrapper>
                                                                    ) : (
                                                                        'Never synced'
                                                                    )
                                                                ) : (
                                                                    'No module'
                                                                )}
                                                                {rowWarnings.some(w => w.type === 'sync') && <WarningIcon level={rowWarnings.find(w => w.type === 'sync')?.level} />}
                                                            </span>
                                                        </div>
                                                        <div>
                                                            <span className="label">Publish:</span>
                                                            <span className="value display-flex-align-center">
                                                                {status.lastPublish ? (
                                                                    <TooltipWrapper text={formatDate(status.lastPublish)} place="top" maxWidth={240}>
                                                                        <span style={{ cursor: 'help' }}>{formatDateShort(status.lastPublish)}</span>
                                                                    </TooltipWrapper>
                                                                ) : (
                                                                    'Never published'
                                                                )}
                                                                {rowWarnings.some(w => w.type === 'publish') && <WarningIcon level={rowWarnings.find(w => w.type === 'publish')?.level} />}
                                                            </span>
                                                        </div>
                                                        <div style={{ gridColumn: 'span 2' }}>
                                                            <span className="label">Telephony Status:</span>
                                                            <span className="value display-flex-align-center" style={{ fontWeight: 600 }}>
                                                                {app.allowsTemplating ? 'N/A' : getTelephonyStatus(status)}
                                                                {rowWarnings.some(w => w.type.startsWith('proactive') || w.type === 'voice' || w.type === 'spanish-mismatch') && <WarningIcon level={rowWarnings.filter(w => w.type.startsWith('proactive') || w.type === 'voice' || w.type === 'spanish-mismatch').some(w => w.level === 'p1') ? 'p1' : 'p2'} />}
                                                            </span>
                                                        </div>
                                                        {!organizationHasDentalApplication && (
                                                            <div style={{ gridColumn: 'span 2' }}>
                                                                <span className='label'>POS Config:</span>
                                                                <span className='value display-flex-align-center' style={{ alignItems: 'flex-start', flexDirection: 'column', gap: '4px' }}>
                                                                    {app.allowsTemplating ? 'N/A' : (status.posConfigIssues || []).length === 0 ? (
                                                                        <span style={{ color: '#238636', fontWeight: 600 }}>Valid</span>
                                                                    ) : (status.posConfigIssues || []).map((issue, index) => (
                                                                        <span key={index} className='display-flex-align-center'>
                                                                            <WarningIcon level={'p1'} />
                                                                            {issue}
                                                                        </span>
                                                                    ))}
                                                                </span>
                                                            </div>
                                                        )}
                                                        <div style={{ gridColumn: 'span 2' }}>
                                                            <span className="label">This Year's Closed Days:</span>
                                                            <span className="value display-flex-align-center" style={{ flexWrap: 'wrap', gap: '4px', flexDirection: 'row', marginTop: '4px' }}>
                                                                {app.allowsTemplating ? 'N/A' : (() => {
                                                                    const sharedAppForFallback = resolvedLocationApps.find(a => a.allowsTemplating);
                                                                    const sharedStatusForFallback = sharedAppForFallback ? locationStatuses[sharedAppForFallback.id!] : undefined;
                                                                    const closedDays = getClosedDaysThisYear(status.disabledDaysRaw || (sharedStatusForFallback ? sharedStatusForFallback.disabledDaysRaw || '' : ''));
                                                                    if (closedDays.length === 0) {
                                                                        return (
                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                                <span style={{ color: '#4a5568' }}>None</span>
                                                                                <TooltipWrapper text="No closed days configured for this year." place="top" maxWidth={240}>
                                                                                    <WarningIcon level="p2" />
                                                                                </TooltipWrapper>
                                                                            </div>
                                                                        );
                                                                    }
                                                                    return closedDays.map((day, idx) => (
                                                                        <ClosedDayPill key={idx}>{day}</ClosedDayPill>
                                                                    ));
                                                                })()}
                                                            </span>
                                                        </div>
                                                    </CardInfoGrid>
                                                </MobileLocationCard>
                                            );
                                        })}
                                    </CardsGrid>

                                    <FooterContainer>
                                        <Button
                                            themes={['primary']}
                                            text={(() => {
                                                if (selectedAppIds.size > 0 && selectedAppIds.size === resolvedLocationApps.length) {
                                                    return "Sync & Publish All";
                                                }
                                                const selectedAppsList = resolvedLocationApps.filter(app => selectedAppIds.has(app.id!));
                                                const onlySharedSelected = selectedAppsList.length > 0 && selectedAppsList.every(app => app.allowsTemplating);
                                                return onlySharedSelected ? `Sync Selected (${selectedAppIds.size})` : `Sync & Publish Selected (${selectedAppIds.size})`;
                                            })()}
                                            onClick={selectedAppIds.size > 0 && selectedAppIds.size === resolvedLocationApps.length ? handleSyncAndPublishAll : handleSyncAndPublishSelected}
                                            disabled={isBulkProcessing || selectedAppIds.size === 0}
                                        />
                                    </FooterContainer>
                                </>
                            )}
                        </>
                    )}
                </ContentCollapsingSection>
            </Container>

            <ToastWrapper>
                {errors.length > 0 && <PageError errors={errors} onClear={() => setErrors([])} type="error" />}
                {successes.length > 0 && <PageError errors={successes} onClear={() => setSuccesses([])} type="success" />}
                {warnings.length > 0 && <PageError errors={warnings} onClear={() => setWarnings([])} type="warning" />}
            </ToastWrapper>

            {/* Right-Side slide-out Detail Drawer */}
            {activeDetailAppId && (() => {
                const activeApp = resolvedLocationApps.find(a => a.id === activeDetailAppId);
                if (!activeApp) return null;

                const activeStatus = locationStatuses[activeDetailAppId] || { lastSync: null, lastPublish: null, status: 'Idle', hasModules: true, todaysHours: null, transferTarget: null, timeZones: [], connections: [], rawDeployments: [], isLoading: true };
                const sharedApp = resolvedLocationApps.find(a => a.allowsTemplating);
                const sharedStatus = sharedApp ? locationStatuses[sharedApp.id!] : undefined;
                const activeWarnings = deriveRowWarnings(activeApp, activeStatus, sharedStatus, organizationHasDentalApplication);
                const activeAppType = getAppType(activeApp);

                // Telephony Slot Allocations
                const inboundEN = (activeStatus.rawDeployments || []).find(d => !d.useForOutboundCalling && (d.locale || '').toLowerCase().startsWith('en'));
                const inboundES = (activeStatus.rawDeployments || []).find(d => !d.useForOutboundCalling && (d.locale || '').toLowerCase().startsWith('es'));
                const outboundEN = (activeStatus.rawDeployments || []).find(d => d.useForOutboundCalling && (d.locale || '').toLowerCase().startsWith('en'));
                const outboundES = (activeStatus.rawDeployments || []).find(d => d.useForOutboundCalling && (d.locale || '').toLowerCase().startsWith('es'));

                // Warning flags for direct highlight
                const isSyncWarning = activeWarnings.some(w => w.type === 'sync');
                const isPublishWarning = activeWarnings.some(w => w.type === 'publish');
                const isVoiceWarning = activeWarnings.some(w => w.type === 'voice');
                const isOutboundProactiveWarning = activeWarnings.some(w => w.type === 'proactive-outbound');

                return (
                    <DrawerOverlay onClick={() => setActiveDetailAppId(null)}>
                        <DrawerContent onClick={(e) => e.stopPropagation()}>
                            <DrawerHeader>
                                <div>
                                    <h3>{activeApp.name}</h3>
                                    <DrawerSubtitleRow style={{ marginTop: '6px' }}>
                                        {!activeApp.allowsTemplating && (
                                            <TooltipWrapper
                                                text={activeApp.isLive ? (activeApp.launchDate ? `Launched: ${formatDateOnly(activeApp.launchDate)}` : 'No launch date set') : (activeApp.targetLaunchDate ? `Target Launch: ${formatDateOnly(activeApp.targetLaunchDate)}` : 'No launch date set')}
                                                place="top"
                                            >
                                                <InlineLivePill isLive={!!activeApp.isLive}>
                                                    {activeApp.isLive ? 'Live' : 'Draft'}
                                                </InlineLivePill>
                                            </TooltipWrapper>
                                        )}
                                        <AppTypeBadge appType={activeAppType}>
                                            {activeAppType === 'Healthcare' ? 'Dental Scheduling' : activeAppType}
                                        </AppTypeBadge>
                                    </DrawerSubtitleRow>
                                </div>
                                <CloseButton onClick={() => setActiveDetailAppId(null)}>&times;</CloseButton>
                            </DrawerHeader>

                            <DrawerBody>
                                {/* Telephony Matrix Section */}
                                <DrawerSection>
                                    <div className="section-title">Telephony Lines Matrix</div>
                                    <MatrixGrid>
                                        {/* 1. Inbound EN */}
                                        <MatrixSlot title="Inbound English">
                                            {inboundEN ? (() => {
                                                const promptExpected = activeAppType === 'Healthcare'
                                                    ? '{{constant.generative_proactive_response_dental}}'
                                                    : '{{constant.proactive_response_prompt}}';
                                                const hasProactive = inboundEN.useGenerativeProactiveResponse || inboundEN.useProactiveResponses;
                                                const currentPrompt = inboundEN.useGenerativeProactiveResponse
                                                    ? inboundEN.generativeProactiveResponsePrompt
                                                    : inboundEN.proactiveResponses;
                                                const isProactiveMismatch = hasProactive && currentPrompt?.trim() !== promptExpected;

                                                return (
                                                    <MatrixDeploymentCard>
                                                        <div className="phone">{inboundEN.phoneNumber || 'N/A'}</div>
                                                        <div className="meta">Virtual: {inboundEN.virtualNumber || 'None'} · {inboundEN.platform || 'Platform'}</div>

                                                        <div className="telemetry-props">
                                                            <div className="telemetry-row">
                                                                <span className="telemetry-label">Voice:</span>
                                                                <EditableValueInDrawer disabled={!inboundEN.phoneNumber} onClick={inboundEN.phoneNumber ? () => startEditVoice(activeApp.id!, inboundEN) : undefined}>
                                                                    <span style={{ display: 'inline-block', maxWidth: '180px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'bottom' }}>{inboundEN.ttsOptions?.[0]?.ttsVoice || inboundEN.ttsVoice || 'Not Configured'}</span>
                                                                </EditableValueInDrawer>
                                                            </div>
                                                            <div className="telemetry-row">
                                                                <span className="telemetry-label">ASR:</span>
                                                                <EditableValueInDrawer disabled={!inboundEN.phoneNumber} onClick={inboundEN.phoneNumber ? () => startEditAsr(activeApp.id!, inboundEN) : undefined}>
                                                                    {inboundEN.asrProvider || 'Deepgram'}
                                                                </EditableValueInDrawer>
                                                            </div>
                                                            <div className="telemetry-row">
                                                                <span className="telemetry-label">Proactive Response:</span>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                    <EditableValueInDrawer disabled={!inboundEN.phoneNumber} onClick={inboundEN.phoneNumber ? () => startEditProactive(activeApp.id!, inboundEN) : undefined}>
                                                                        <span style={{ display: 'inline-block', maxWidth: '160px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'bottom' }}>{hasProactive && currentPrompt ? currentPrompt : 'Not Configured'}</span>
                                                                    </EditableValueInDrawer>
                                                                    {isProactiveMismatch ? (
                                                                        <TooltipWrapper text={`Should use standard constant: ${promptExpected}`} place="top" maxWidth={240}>
                                                                            <WarningIcon level="p2" style={{ width: '14px', height: '14px' }} />
                                                                        </TooltipWrapper>
                                                                    ) : (!hasProactive || !currentPrompt || currentPrompt.trim() === '' || currentPrompt.trim().toLowerCase() === 'none') ? (
                                                                        <TooltipWrapper text="No proactive response value configured." place="top" maxWidth={240}>
                                                                            <WarningIcon level="p1" style={{ width: '14px', height: '14px' }} />
                                                                        </TooltipWrapper>
                                                                    ) : null}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </MatrixDeploymentCard>
                                                );
                                            })() : (
                                                <MatrixEmptyCard>Not Configured</MatrixEmptyCard>
                                            )}
                                        </MatrixSlot>

                                        {/* 2. Inbound ES */}
                                        <MatrixSlot title="Inbound Spanish">
                                            {inboundES ? (() => {
                                                const promptExpected = activeAppType === 'Healthcare'
                                                    ? '{{constant.generative_proactive_response_dental_spanish}}'
                                                    : '{{constant.generative_proactive_response_ordering_spanish}}';
                                                const hasProactive = inboundES.useGenerativeProactiveResponse || inboundES.useProactiveResponses;
                                                const currentPrompt = inboundES.useGenerativeProactiveResponse
                                                    ? inboundES.generativeProactiveResponsePrompt
                                                    : inboundES.proactiveResponses;
                                                const isProactiveMismatch = hasProactive && currentPrompt?.trim() !== promptExpected;

                                                return (
                                                    <MatrixDeploymentCard>
                                                        <div className="phone">{inboundES.phoneNumber || 'N/A'}</div>
                                                        <div className="meta">Virtual: {inboundES.virtualNumber || 'None'} · {inboundES.platform || 'Platform'}</div>

                                                        <div className="telemetry-props">
                                                            <div className="telemetry-row">
                                                                <span className="telemetry-label">Voice:</span>
                                                                <EditableValueInDrawer disabled={!inboundES.phoneNumber} onClick={inboundES.phoneNumber ? () => startEditVoice(activeApp.id!, inboundES) : undefined}>
                                                                    <span style={{ display: 'inline-block', maxWidth: '180px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'bottom' }}>{inboundES.ttsOptions?.[0]?.ttsVoice || inboundES.ttsVoice || 'Not Configured'}</span>
                                                                </EditableValueInDrawer>
                                                            </div>
                                                            <div className="telemetry-row">
                                                                <span className="telemetry-label">ASR:</span>
                                                                <EditableValueInDrawer disabled={!inboundES.phoneNumber} onClick={inboundES.phoneNumber ? () => startEditAsr(activeApp.id!, inboundES) : undefined}>
                                                                    {inboundES.asrProvider || 'Deepgram'}
                                                                </EditableValueInDrawer>
                                                            </div>
                                                            <div className="telemetry-row">
                                                                <span className="telemetry-label">Proactive Response:</span>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                    <EditableValueInDrawer disabled={!inboundES.phoneNumber} onClick={inboundES.phoneNumber ? () => startEditProactive(activeApp.id!, inboundES) : undefined}>
                                                                        <span style={{ display: 'inline-block', maxWidth: '160px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'bottom' }}>{hasProactive && currentPrompt ? currentPrompt : 'Not Configured'}</span>
                                                                    </EditableValueInDrawer>
                                                                    {isProactiveMismatch ? (
                                                                        <TooltipWrapper text={`Should use standard constant: ${promptExpected}`} place="top" maxWidth={240}>
                                                                            <WarningIcon level="p2" style={{ width: '14px', height: '14px' }} />
                                                                        </TooltipWrapper>
                                                                    ) : (!hasProactive || !currentPrompt || currentPrompt.trim() === '' || currentPrompt.trim().toLowerCase() === 'none') ? (
                                                                        <TooltipWrapper text="No proactive response value configured." place="top" maxWidth={240}>
                                                                            <WarningIcon level="p1" style={{ width: '14px', height: '14px' }} />
                                                                        </TooltipWrapper>
                                                                    ) : null}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </MatrixDeploymentCard>
                                                );
                                            })() : (
                                                <MatrixEmptyCard>Not Configured</MatrixEmptyCard>
                                            )}
                                        </MatrixSlot>

                                        {activeAppType === 'Healthcare' && (
                                            <React.Fragment>
                                                {/* 3. Outbound EN */}
                                                <MatrixSlot title="Outbound English">
                                                    {outboundEN ? (() => {
                                                        const hasProactive = outboundEN.useGenerativeProactiveResponse || outboundEN.useProactiveResponses;
                                                        const currentPrompt = outboundEN.useGenerativeProactiveResponse
                                                            ? outboundEN.generativeProactiveResponsePrompt
                                                            : outboundEN.proactiveResponses;

                                                        return (
                                                            <MatrixDeploymentCard>
                                                                <div className="phone">{outboundEN.phoneNumber || 'N/A'}</div>
                                                                <div className="meta">Caller ID: {outboundEN.outboundCallerIdNumber || 'None'} · {outboundEN.platform || 'Platform'}</div>

                                                                <div className="telemetry-props">
                                                                    <div className="telemetry-row">
                                                                        <span className="telemetry-label">Voice:</span>
                                                                        <EditableValueInDrawer disabled={!outboundEN.phoneNumber} onClick={outboundEN.phoneNumber ? () => startEditVoice(activeApp.id!, outboundEN) : undefined}>
                                                                            <span style={{ display: 'inline-block', maxWidth: '180px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'bottom' }}>{outboundEN.ttsOptions?.[0]?.ttsVoice || outboundEN.ttsVoice || 'Not Configured'}</span>
                                                                        </EditableValueInDrawer>
                                                                    </div>
                                                                    <div className="telemetry-row">
                                                                        <span className="telemetry-label">ASR:</span>
                                                                        <EditableValueInDrawer disabled={!outboundEN.phoneNumber} onClick={outboundEN.phoneNumber ? () => startEditAsr(activeApp.id!, outboundEN) : undefined}>
                                                                            {outboundEN.asrProvider || 'Deepgram'}
                                                                        </EditableValueInDrawer>
                                                                    </div>
                                                                    <div className="telemetry-row">
                                                                        <span className="telemetry-label">Proactive Response:</span>
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                            <EditableValueInDrawer disabled={!outboundEN.phoneNumber} onClick={outboundEN.phoneNumber ? () => startEditProactive(activeApp.id!, outboundEN) : undefined}>
                                                                                <span style={{ display: 'inline-block', maxWidth: '160px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'bottom' }}>{hasProactive && currentPrompt ? currentPrompt : 'Not Configured'}</span>
                                                                            </EditableValueInDrawer>
                                                                            {hasProactive && (
                                                                                <TooltipWrapper text="Outbound lines should not use proactive responses." place="top" maxWidth={240}>
                                                                                    <WarningIcon level="p2" style={{ width: '14px', height: '14px' }} />
                                                                                </TooltipWrapper>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </MatrixDeploymentCard>
                                                        );
                                                    })() : (
                                                        <MatrixEmptyCard>Not Configured</MatrixEmptyCard>
                                                    )}
                                                </MatrixSlot>

                                                {/* 4. Outbound ES */}
                                                <MatrixSlot title="Outbound Spanish">

                                                    {outboundES ? (() => {
                                                        const hasProactive = outboundES.useGenerativeProactiveResponse || outboundES.useProactiveResponses;
                                                        const currentPrompt = outboundES.useGenerativeProactiveResponse
                                                            ? outboundES.generativeProactiveResponsePrompt
                                                            : outboundES.proactiveResponses;

                                                        return (
                                                            <MatrixDeploymentCard>
                                                                <div className="phone">{outboundES.phoneNumber || 'N/A'}</div>
                                                                <div className="meta">Caller ID: {outboundES.outboundCallerIdNumber || 'None'} · {outboundES.platform || 'Platform'}</div>

                                                                <div className="telemetry-props">
                                                                    <div className="telemetry-row">
                                                                        <span className="telemetry-label">Voice:</span>
                                                                        <EditableValueInDrawer disabled={!outboundES.phoneNumber} onClick={outboundES.phoneNumber ? () => startEditVoice(activeApp.id!, outboundES) : undefined}>
                                                                            <span style={{ display: 'inline-block', maxWidth: '180px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'bottom' }}>{outboundES.ttsOptions?.[0]?.ttsVoice || outboundES.ttsVoice || 'Not Configured'}</span>
                                                                        </EditableValueInDrawer>
                                                                    </div>
                                                                    <div className="telemetry-row">
                                                                        <span className="telemetry-label">ASR:</span>
                                                                        <EditableValueInDrawer disabled={!outboundES.phoneNumber} onClick={outboundES.phoneNumber ? () => startEditAsr(activeApp.id!, outboundES) : undefined}>
                                                                            {outboundES.asrProvider || 'Deepgram'}
                                                                        </EditableValueInDrawer>
                                                                    </div>
                                                                    <div className="telemetry-row">
                                                                        <span className="telemetry-label">Proactive Response:</span>
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                            <EditableValueInDrawer disabled={!outboundES.phoneNumber} onClick={outboundES.phoneNumber ? () => startEditProactive(activeApp.id!, outboundES) : undefined}>
                                                                                <span style={{ display: 'inline-block', maxWidth: '160px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'bottom' }}>{hasProactive && currentPrompt ? currentPrompt : 'Not Configured'}</span>
                                                                            </EditableValueInDrawer>
                                                                            {hasProactive && (
                                                                                <TooltipWrapper text="Outbound lines should not use proactive responses." place="top" maxWidth={240}>
                                                                                    <WarningIcon level="p2" style={{ width: '14px', height: '14px' }} />
                                                                                </TooltipWrapper>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </MatrixDeploymentCard>
                                                        );
                                                    })() : (
                                                        <MatrixEmptyCard>Not Configured</MatrixEmptyCard>
                                                    )}
                                                </MatrixSlot>
                                            </React.Fragment>
                                        )}
                                    </MatrixGrid>
                                </DrawerSection>
                            </DrawerBody>
                        </DrawerContent>
                    </DrawerOverlay>
                );
            })()}

            {/* Connection Edit Modal / Gen AI Drawer */}
            {editingAppId && editType && (
                editType === 'genai' ? (
                    <DrawerOverlay onClick={closeTelemetryModal}>
                        <DrawerContent onClick={(e) => e.stopPropagation()}>
                            <DrawerHeader>
                                <h3>Edit Gen AI Settings</h3>
                                <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', marginTop: '4px' }}>
                                    {resolvedLocationApps.find(a => a.id === editingAppId)?.name || 'Unknown Location'}
                                </div>
                                <CloseButton onClick={closeTelemetryModal}>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                                </CloseButton>
                            </DrawerHeader>
                            <DrawerBody>
                                <DrawerSection>
                                    {isLoadingOnDemand ? (
                                        <LoaderContainer style={{ padding: '24px 0' }}>
                                            <Loader />
                                            <LoadingText>Loading active configurations from server...</LoadingText>
                                        </LoaderContainer>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                <label style={{ fontWeight: 600, fontSize: '14px', color: '#101d2c' }}>Gen AI Model</label>
                                                <FormDropdown
                                                    placeholder="Choose Model..."
                                                    value={editGenAIModel}
                                                    onChange={(val) => setEditGenAIModel(val)}
                                                    options={GenAIModels.map(opt => ({ label: opt.label, value: opt.value }))}
                                                />
                                            </div>
                                            {getAppType(resolvedLocationApps.find(a => a.id === editingAppId)!) !== 'Healthcare' && (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                    <label style={{ fontWeight: 600, fontSize: '14px', color: '#101d2c' }}>Ordering GenAI Prompt Override</label>
                                                    <textarea
                                                        rows={8}
                                                        placeholder="Enter prompting to override food ordering GenAI model behavior..."
                                                        value={editGenAIPrompt}
                                                        onChange={(e) => setEditGenAIPrompt(e.target.value)}
                                                        style={{
                                                            width: '100%',
                                                            padding: '8px 12px',
                                                            border: `1px solid ${silver_two}`,
                                                            borderRadius: '4px',
                                                            fontSize: '13px',
                                                            fontFamily: 'Muli',
                                                            resize: 'vertical'
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </DrawerSection>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', padding: '16px 24px', borderTop: `1px solid ${silver_two}` }}>
                                    <Button themes={['secondary-small']} text="Cancel" onClick={closeTelemetryModal} />
                                    <Button themes={['primary-small']} text={isSavingTelemetry ? 'Saving...' : 'Save Changes'} onClick={handleSaveTelemetrySettings} disabled={isSavingTelemetry || isLoadingOnDemand} />
                                </div>
                            </DrawerBody>
                        </DrawerContent>
                    </DrawerOverlay>
                ) : (
                    <ModalOverlay onClick={closeTelemetryModal}>
                        <ModalContent onClick={(e) => e.stopPropagation()}>
                            <ModalTitle>
                                Edit {editType === 'asr' ? 'ASR Provider' : (editType === 'proactive' ? 'Proactive Response' : (editType === 'voice' ? 'Voice' : (editType === 'genaiModel' ? 'Ordering GenAI Model' : 'Ordering GenAI Prompt Override')))}
                            </ModalTitle>
                            {editingDeployment && (
                                <div style={{ fontSize: '13px', color: '#666', marginTop: '-8px' }}>
                                    Connection: {editingDeployment.phoneNumber}
                                </div>
                            )}

                            {isLoadingOnDemand ? (
                                <LoaderContainer style={{ padding: '24px 0' }}>
                                    <Loader />
                                    <LoadingText>Loading active configurations from server...</LoadingText>
                                </LoaderContainer>
                            ) : (
                                <>
                                    {editType === 'genaiModel' && (
                                        <ModalFormGroup>
                                            <label>Ordering GenAI Model</label>
                                            <select
                                                value={editGenAIModel}
                                                onChange={(e) => setEditGenAIModel(e.target.value)}
                                            >
                                                {GenAIModels.map(model => (
                                                    <option key={model.value} value={model.value}>
                                                        {model.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </ModalFormGroup>
                                    )}

                                    {editType === 'genaiPrompt' && (
                                        <ModalFormGroup>
                                            <label>Ordering GenAI Prompt Override</label>
                                            <textarea
                                                rows={8}
                                                placeholder="Enter prompting to override food ordering GenAI model behavior..."
                                                value={editGenAIPrompt}
                                                onChange={(e) => setEditGenAIPrompt(e.target.value)}
                                            />
                                        </ModalFormGroup>
                                    )}

                                    {editType === 'asr' && (
                                        <ModalFormGroup>
                                            <label>ASR Provider</label>
                                            <select
                                                value={editAsrProvider}
                                                onChange={(e) => setEditAsrProvider(e.target.value)}
                                            >
                                                <option value="Deepgram">Deepgram</option>
                                                <option value="Soniox">Soniox</option>
                                                <option value="Google">Google</option>
                                            </select>
                                        </ModalFormGroup>
                                    )}

                                    {editType === 'proactive' && (
                                        <ModalFormGroup>
                                            <label>Proactive Response</label>
                                            <textarea
                                                rows={5}
                                                placeholder="Enter prompting to generate proactive responses..."
                                                value={editProactivePrompt}
                                                onChange={(e) => setEditProactivePrompt(e.target.value)}
                                            />
                                        </ModalFormGroup>
                                    )}

                                    {editType === 'voice' && (
                                        <ModalFormGroup>
                                            <label>Voice Selection</label>
                                            <select
                                                value={voiceOptions.some(o => o.voice === editVoiceString) ? editVoiceString : 'custom'}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    if (val !== 'custom') {
                                                        setEditVoiceString(val);
                                                    } else {
                                                        setEditVoiceString('');
                                                    }
                                                }}
                                            >
                                                {voiceOptions.map(opt => (
                                                    <option key={opt.voice} value={opt.voice}>{opt.label}</option>
                                                ))}
                                                <option value="custom">Custom Voice String</option>
                                            </select>

                                            {(!voiceOptions.some(o => o.voice === editVoiceString) || editVoiceString === '') && (
                                                <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                    <label style={{ fontSize: '11px', color: '#777' }}>ElevenLabs Custom Voice String</label>
                                                    <textarea
                                                        rows={3}
                                                        placeholder="voice_id=...|model_id=..."
                                                        value={editVoiceString}
                                                        onChange={(e) => setEditVoiceString(e.target.value)}
                                                    />
                                                </div>
                                            )}
                                        </ModalFormGroup>
                                    )}
                                </>
                            )}

                            <ModalButtons>
                                <Button
                                    themes={['secondary-small']}
                                    text="Cancel"
                                    onClick={closeTelemetryModal}
                                    disabled={isSavingTelemetry || isLoadingOnDemand}
                                />
                                <Button
                                    themes={['primary-small']}
                                    text={isSavingTelemetry ? 'Saving...' : 'Save Settings'}
                                    onClick={handleSaveTelemetrySettings}
                                    disabled={isSavingTelemetry || isLoadingOnDemand}
                                />
                            </ModalButtons>
                        </ModalContent>
                    </ModalOverlay>
                )
            )}
        </SettingsCard>
    );
};

// Styles and overrides
const settingsCardBodyStyle = css`
    padding: 0px !important;
    overflow: hidden;
    border-radius: 0 0 10px 10px;
`;

const collapsingSectionStyle = css`
    border-top: solid 1px ${silver_two} !important;

    &:first-of-type {
        border-top: none !important;
    }

    && .card-title-container {
        padding: 24px 32px;
        background-color: #fdfdfd;
        border-bottom: none;

        .card-hover-icon {
            display: none;
        }
    }

    && .card-title-container.expanded {
        border-bottom: 1px solid ${silver_two};
    }

    && .card-body.expanded {
        padding: 24px 32px;
    }

    @media (max-width: 768px) {
        && .card-title-container {
            padding: 16px;
        }
        && .card-body.expanded {
            padding: 16px;
        }
    }
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0;
    padding-bottom: 32px;
`;

const SaveButtonWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;
    button {
        margin: 0;
    }
`;

const LocationManagerLoaderContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-height: 450px;
    gap: 16px;
    background-color: #ffffff;
    position: relative;
`;

const LoadingText = styled.p`
    font-family: Muli;
    font-size: 14px;
    color: ${dark_grey_blue};
    margin: 0;
`;

const EmptyState = styled.div`
    font-family: Muli;
    font-size: 14px;
    color: #777;
    text-align: center;
    padding: 48px;
    border: dashed 1px ${silver_two};
    border-radius: 8px;
    margin: 24px 0;
`;

const ToolbarContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    margin-bottom: 24px;
    flex-wrap: wrap;
`;

const ToolbarLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
`;

const ToolbarRight = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

const FilterInputWrapper = styled.div`
    width: 200px;
    @media (max-width: 576px) {
        width: 100%;
    }
`;

const ToolbarInput = styled.input`
    width: 100%;
    padding: 8px 16px;
    border: 1px solid ${silver_two};
    border-radius: 20px;
    font-family: Muli;
    font-size: 13px;
    outline: none;
    box-sizing: border-box;
    transition: all 0.2s ease-in-out;

    &:hover {
        border-color: #cbd5e0;
    }

    &:focus {
        border-color: #007dbb;
        box-shadow: 0 0 0 3px rgba(0, 125, 187, 0.15);
    }
`;

const DropdownWrapper = styled.div`
    position: relative;
    display: inline-block;
    width: 100%;
`;

const DropdownHeaderButton = styled.button<{ isActive?: boolean }>`
    background: none;
    border: none;
    font-family: Muli;
    font-size: 13px;
    font-weight: 700;
    color: ${props => props.isActive ? '#007dbb' : '#4a5568'};
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 0;
    outline: none;
    transition: all 0.15s ease;

    span {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .chevron {
        margin-left: 6px;
        flex-shrink: 0;
        transition: transform 0.2s;
    }

    &:hover {
        color: ${props => props.isActive ? '#0056b3' : '#007dbb'};
    }
`;

const DropdownMenu = styled.div<{ alignRight?: boolean }>`
    position: absolute;
    top: calc(100% + 6px);
    ${props => props.alignRight ? 'right: 0;' : 'left: 0;'}
    min-width: 220px;
    background-color: #ffffff;
    border: 1px solid ${silver_two};
    border-radius: 8px;
    box-shadow: 0 6px 16px rgba(48, 69, 98, 0.12);
    z-index: 1000;
    padding: 6px 0;
    display: flex;
    flex-direction: column;
    animation: fadeIn 0.15s ease-out;

    @keyframes fadeIn {
        from {
            opacity: 0;
            transform: translateY(-4px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
`;

const DropdownMenuItem = styled.button<{ selected?: boolean }>`
    background: none;
    border: none;
    width: 100%;
    padding: 8px 16px;
    text-align: left;
    font-family: Muli;
    font-size: 13px;
    font-weight: ${props => props.selected ? '700' : '400'};
    color: ${props => props.selected ? '#007dbb' : '#2d3748'};
    background-color: ${props => props.selected ? '#ebf8ff' : 'transparent'};
    cursor: pointer;
    outline: none;
    transition: all 0.15s;

    &:hover {
        background-color: #f7fafc;
        color: #007dbb;
    }
`;

const FormDropdownWrapper = styled.div`
    position: relative;
    display: inline-block;
    width: 100%;
`;

const FormDropdownButton = styled.button<{ hasValue?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: 38px;
    padding: 0 16px;
    border: 1px solid ${silver_two};
    border-radius: 20px;
    font-family: Muli;
    font-size: 13px;
    font-weight: ${props => props.hasValue ? '700' : '400'};
    color: ${props => props.hasValue ? '#2d3748' : '#718096'};
    background-color: #ffffff;
    cursor: pointer;
    outline: none;
    transition: all 0.2s ease-in-out;

    span {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .chevron {
        margin-left: 8px;
        color: #718096;
        flex-shrink: 0;
    }

    &:hover:not(:disabled) {
        border-color: #cbd5e0;
    }

    &:focus:not(:disabled) {
        border-color: #007dbb;
        box-shadow: 0 0 0 3px rgba(0, 125, 187, 0.15);
    }

    &:disabled {
        background-color: #edf2f7;
        cursor: not-allowed;
        opacity: 0.6;
    }
`;

const FormDropdownMenu = styled.div`
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    width: 100%;
    max-height: 240px;
    overflow-y: auto;
    background-color: #ffffff;
    border: 1px solid ${silver_two};
    border-radius: 8px;
    box-shadow: 0 6px 16px rgba(48, 69, 98, 0.12);
    z-index: 1000;
    padding: 6px 0;
    display: flex;
    flex-direction: column;
    animation: fadeIn 0.15s ease-out;

    @keyframes fadeIn {
        from {
            opacity: 0;
            transform: translateY(-4px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
`;

const FormDropdownMenuItem = styled.button<{ selected?: boolean }>`
    background: none;
    border: none;
    width: 100%;
    padding: 8px 16px;
    text-align: left;
    font-family: Muli;
    font-size: 13px;
    font-weight: ${props => props.selected ? '700' : '400'};
    color: ${props => props.selected ? '#007dbb' : '#2d3748'};
    background-color: ${props => props.selected ? '#ebf8ff' : 'transparent'};
    cursor: pointer;
    outline: none;
    transition: all 0.15s;

    &:hover {
        background-color: #f7fafc;
        color: #007dbb;
    }
`;

const SortButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 2px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    outline: none;
    margin-left: 2px;
`;

const ToolbarSelect = styled.select`
    padding: 8px 36px 8px 16px;
    border: 1px solid ${silver_two};
    border-radius: 20px;
    font-family: Muli;
    font-size: 13px;
    outline: none;
    background-color: #ffffff;
    cursor: pointer;
    min-width: 140px;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23718096' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-position: right 14px center;
    background-size: 13px;
    transition: all 0.2s ease-in-out;

    &:hover {
        border-color: #cbd5e0;
    }

    &:focus {
        border-color: #007dbb;
        box-shadow: 0 0 0 3px rgba(0, 125, 187, 0.15);
    }

    option {
        padding: 10px;
        border-radius: 8px;
        font-family: Muli;
        font-size: 13px;
        background-color: #ffffff;
        color: #2d3748;
    }

    @media (max-width: 576px) {
        width: 100%;
    }
`;

const SelectAllLiveButton = styled.button`
    background: none;
    border: none;
    color: #007dbb;
    font-family: Muli;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    border-bottom: 1px dashed #007dbb;
    padding: 0;
    margin-right: 8px;
    &:hover {
        color: #0056b3;
        border-bottom-color: #0056b3;
    }
`;

const BulkButtonsGroup = styled.div`
    display: flex;
    gap: 8px;
    button {
        margin: 0;
    }
`;

const LogSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 24px;
`;

const LogHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-family: Muli;
    font-size: 14px;
    font-weight: 700;
    color: ${dark_grey_blue};
    button {
        margin: 0;
    }
`;

const LogArea = styled.div`
    max-height: 140px;
    overflow-y: auto;
    padding: 12px 16px;
    background: #fdfdfd;
    border: 1px solid ${silver_two};
    border-radius: 6px;
    font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
    font-size: 11px;
    color: #666;
    line-height: 1.5;
`;

const LogLine = styled.div`
    margin-bottom: 4px;
    word-break: break-all;
    white-space: pre-wrap;
`;

const TableContainer = styled.div`
    width: 100%;
    overflow-x: auto;
    border: 1px solid ${silver_two};
    border-radius: 8px;
    background-color: #ffffff;

    &.desktop-only {
        @media (max-width: 768px) {
            display: none;
        }
    }
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-family: Muli;
    table-layout: fixed;

    tbody tr {
        transition: background-color 0.15s ease-in-out;
        &:hover {
            background-color: #f8fafc;
        }
    }
`;

const Th = styled.th`
    background-color: #edf2f7;
    border-bottom: 1px solid ${silver_two};
    color: #4a5568;
    font-size: 13px;
    font-weight: 700;
    padding: 12px 16px;
    text-align: left;
`;

const Td = styled.td`
    padding: 12px 16px;
    border-bottom: 1px solid ${silver_two};
    font-size: 13px;
    color: #2d3748;
    vertical-align: middle;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const CellRow = styled.div`
    font-size: 13px;
    font-weight: 400;
    color: #444;
    margin-bottom: 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const CellSpinner = styled.div`
    display: inline-block;
    width: 14px;
    height: 14px;
    border: 2px solid #edf2f7;
    border-radius: 50%;
    border-top-color: #3182ce;
    animation: spin 0.8s linear infinite;

    @keyframes spin {
        to {
            transform: rotate(360deg);
        }
    }
`;

const ThContent = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    user-select: none;
    &:hover {
        color: #007dbb;
    }
`;

const SortIndicator = styled.span`
    font-size: 11px;
    color: #a0aec0;
`;

const LocationLink = styled.a`
    font-size: 13px;
    font-weight: 700;
    color: ${dark_grey_blue};
    text-decoration: none;
    transition: all 0.2s;
    border-bottom: 1px dashed transparent;

    &:hover {
        color: #007dbb;
        border-bottom-color: #007dbb;
    }
`;

const InlineLivePill = styled.span<{ isLive: boolean }>`
    display: inline-block;
    padding: 3px 10px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background-color: ${props => props.isLive ? '#E8F5E9' : '#edf2f7'};
    color: ${props => props.isLive ? '#58BD99' : '#4a5568'};
    border: 1px solid ${props => props.isLive ? '#A5D6A7' : '#cbd5e0'};
    transition: all 0.2s;
`;

const AppTypeBadge = styled.span<{ appType: 'Ordering' | 'Healthcare' | 'AI Answering' }>`
    display: inline-block;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 700;
    background-color: ${props => {
        if (props.appType === 'Ordering') return '#ebf8ff';
        if (props.appType === 'Healthcare') return '#faf5ff';
        return '#f7fafc';
    }};
    color: ${props => {
        if (props.appType === 'Ordering') return '#2b6cb0';
        if (props.appType === 'Healthcare') return '#553c9a';
        return '#4a5568';
    }};
    border: 1px solid ${props => {
        if (props.appType === 'Ordering') return '#bee3f8';
        if (props.appType === 'Healthcare') return '#e9d8fd';
        return '#e2e8f0';
    }};
`;

const TelephonyCompactSummary = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-size: 12px;
    color: #4a5568;
`;

const alertTriangleIcon = require('../../../../../shared/content/images/alert-triangle-yellow.svg');
const alertTriangleRedIcon = require('../../../../../shared/content/images/alert-triangle-red.svg');

const WarningIconSvg = styled.svg<{ level?: 'p1' | 'p2' }>`
    width: 14px;
    height: 14px;
    cursor: help;
    margin-left: 6px;
    display: inline-block;
    vertical-align: middle;
    flex-shrink: 0;
`;

const WarningIcon = React.forwardRef<SVGSVGElement, React.SVGProps<SVGSVGElement> & { level?: 'p1' | 'p2' }>(({ level, ...props }, ref) => (
    <WarningIconSvg
        ref={ref}
        viewBox="0 0 24 24"
        width="24"
        height="24"
        stroke={level === 'p1' ? '#c53030' : '#d69e2e'}
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        level={level}
        {...props}
    >
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
    </WarningIconSvg>
));

const WarningPill = styled.span<{ type: string; level?: 'p1' | 'p2' }>`
    display: inline-block;
    padding: 2px 6px;
    border-radius: 12px;
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    box-sizing: border-box;
    white-space: nowrap;
    
    background-color: ${props => props.level === 'p1' ? '#fff5f5' : '#fffaf0'};
    color: ${props => props.level === 'p1' ? '#c53030' : '#dd6b20'};
    border: 1px solid ${props => props.level === 'p1' ? '#feb2b2' : '#feebc8'};
`;

const WarningPillsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    justify-content: flex-end;
`;

/* Cards grid for mobile */
const CardsGrid = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;

    &.mobile-only {
        display: none;
        @media (max-width: 768px) {
            display: flex;
        }
    }
`;

const MobileLocationCard = styled.div`
    background-color: #ffffff;
    border: 1px solid ${silver_two};
    border-radius: 8px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    cursor: pointer;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    &:hover {
        border-color: #cbd5e0;
    }
`;

const CardTopRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

const CardCheckboxWrapper = styled.div`
    display: flex;
    align-items: center;
`;

const CardTitleLink = styled.a`
    font-size: 14px;
    font-weight: 700;
    color: ${dark_grey_blue};
    text-decoration: none;
    margin-right: auto;
    &:hover {
        color: #007dbb;
    }
`;

const CardBodyRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const CardInfoGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    background-color: #f7fafc;
    padding: 12px;
    border-radius: 6px;
    font-size: 12px;

    div {
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

    .label {
        font-weight: 700;
        color: #718096;
    }

    .value {
        color: #2d3748;
    }

    .display-flex-align-center {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 4px;
    }
`;

/* Drawer styles */
const DrawerOverlay = styled.div`
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: rgba(0, 0, 0, 0.4);
    z-index: 1000;
    display: flex;
    justify-content: flex-end;
`;

const DrawerContent = styled.div`
    width: 500px;
    max-width: 100%;
    height: 100%;
    background-color: #ffffff;
    box-shadow: -4px 0 24px rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
    animation: slideIn 0.25s ease-out;

    @keyframes slideIn {
        from {
            transform: translateX(100%);
        }
        to {
            transform: translateX(0);
        }
    }
`;

const DrawerHeader = styled.div`
    padding: 24px;
    border-bottom: 1px solid ${silver_two};
    display: flex;
    justify-content: space-between;
    align-items: flex-start;

    h3 {
        margin: 0;
        font-family: Muli;
        font-size: 20px;
        font-weight: 700;
        color: ${dark_grey_blue};
    }
`;

const DrawerSubtitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: #718096;
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    font-size: 28px;
    cursor: pointer;
    color: ${color_shades_dark};
    padding: 0;
    line-height: 1;
    &:hover {
        color: ${dark_grey_blue};
    }
`;

const DrawerBody = styled.div`
    padding: 24px;
    overflow-y: auto;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    gap: 24px;
    background-color: #f7fafc;
`;

const DrawerWarningsPanel = styled.div`
    background-color: #fff5f5;
    border: 1px solid #fed7d7;
    border-radius: 8px;
    padding: 16px;

    .title {
        font-family: Muli;
        font-size: 14px;
        font-weight: 700;
        color: #c53030;
        margin-bottom: 8px;
    }

    .list {
        margin: 0;
        padding-left: 20px;
        font-size: 13px;
        color: #9b2c2c;
        line-height: 1.4;
    }
`;

const DrawerSection = styled.div`
    background-color: #ffffff;
    border: 1px solid ${silver_two};
    border-radius: 8px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;

    .section-title {
        font-family: Muli;
        font-size: 14px;
        font-weight: 700;
        color: ${dark_grey_blue};
        border-bottom: 1px solid #f7fafc;
        padding-bottom: 8px;
        margin-bottom: 4px;
    }

    .display-flex-between {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }
`;

const DrawerCardGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
`;

const DrawerStatusCard = styled.div<{ error?: boolean }>`
    background-color: ${props => props.error ? '#fffaf0' : '#f7fafc'};
    border: 1px solid ${props => props.error ? '#feebc8' : '#e2e8f0'};
    border-radius: 6px;
    padding: 12px;

    .label {
        font-size: 11px;
        font-weight: 700;
        color: ${props => props.error ? '#dd6b20' : '#718096'};
        text-transform: uppercase;
        margin-bottom: 4px;
    }

    .value {
        font-size: 13px;
        font-weight: 600;
        color: #2d3748;
    }
`;

const DrawerInlineActions = styled.div`
    display: flex;
    justify-content: flex-end;
    button {
        margin: 0;
    }
`;

const MatrixGrid = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const MatrixSlotContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;

    .slot-title {
        font-size: 12px;
        font-weight: 700;
        color: #718096;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }
`;

const MatrixSlot: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <MatrixSlotContainer>
        <span className="slot-title">{title}</span>
        {children}
    </MatrixSlotContainer>
);

const MatrixDeploymentCard = styled.div`
    background-color: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 6px;

    .phone {
        font-size: 14px;
        font-weight: 700;
        color: #2d3748;
    }

    .meta {
        font-size: 11px;
        color: #718096;
    }

    .telemetry-props {
        display: flex;
        flex-direction: column;
        gap: 6px;
        margin-top: 4px;
        border-top: 1px dashed #edf2f7;
        padding-top: 6px;
    }

    .telemetry-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 12px;
    }

    .telemetry-label {
        font-weight: 700;
        color: #4a5568;
    }
`;

const MatrixEmptyCard = styled.div`
    background-color: #f7fafc;
    border: 1px dashed #cbd5e0;
    border-radius: 6px;
    padding: 12px;
    font-size: 12px;
    color: #a0aec0;
    text-align: center;
    font-style: italic;
`;

const ModuleFieldsTable = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;

    .row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 13px;
        padding-bottom: 6px;
        border-bottom: 1px solid #edf2f7;
        &:last-child {
            border-bottom: none;
            padding-bottom: 0;
        }
    }

    .label {
        font-weight: 700;
        color: #4a5568;
    }

    .value {
        color: #2d3748;
    }
`;

const SparkFormLink = styled.a`
    font-size: 12px;
    color: #007dbb;
    text-decoration: none;
    font-weight: 700;
    border-bottom: 1px dashed #007dbb;
    &:hover {
        color: #0056b3;
        border-bottom-color: #0056b3;
    }
`;



const EditableValue = styled.span`
    cursor: pointer;
    border-bottom: 1px dashed ${silver_two};
    transition: all 0.2s;
    &:hover {
        color: #007dbb;
        border-bottom-color: #007dbb;
    }
`;

const EditableValueInDrawer = styled.span<{ error?: boolean; disabled?: boolean }>`
    cursor: ${props => props.disabled ? 'default' : 'pointer'};
    color: ${props => props.disabled ? '#4a5568' : (props.error ? '#c53030' : '#2b6cb0')};
    text-decoration: none;
    border-bottom: ${props => props.disabled ? 'none' : (props.error ? '1px dashed #feb2b2' : '1px dashed #bee3f8')};
    padding-bottom: 1px;
    display: inline-block;
    max-width: 180px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    vertical-align: bottom;

    &:hover {
        color: ${props => props.disabled ? '#4a5568' : (props.error ? '#9b2c2c' : '#2c5282')};
        border-bottom-style: ${props => props.disabled ? 'none' : 'solid'};
    }
`;

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2000;
`;

const ModalContent = styled.div`
    background: white;
    padding: 32px;
    border-radius: 12px;
    width: 460px;
    display: flex;
    flex-direction: column;
    gap: 20px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
`;

const ModalTitle = styled.h4`
    font-family: Muli;
    font-size: 20px;
    font-weight: 700;
    color: ${dark_grey_blue};
    margin: 0;
`;

const ModalFormGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    
    label {
        font-family: Muli;
        font-size: 13px;
        font-weight: 700;
        color: #555;
    }

    select, textarea, input {
        padding: 10px 14px;
        border-radius: 6px;
        border: solid 1px ${silver_two};
        outline: none;
        font-family: Muli;
        font-size: 14px;
        &:focus {
            border-color: #007dbb;
        }
    }
`;

const ModalButtons = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 8px;
    button {
        margin: 0;
    }
`;

const FooterContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
    margin-top: 24px;
    width: 100%;
    button {
        margin: 0;
    }
`;

const SearchContainer = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
`;

const ClearSearchButton = styled.button`
    position: absolute;
    right: 12px;
    background: none;
    border: none;
    font-size: 16px;
    font-weight: bold;
    color: #a0aec0;
    cursor: pointer;
    padding: 4px;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: all 0.2s;
    outline: none;

    &:hover {
        color: #4a5568;
        background-color: #edf2f7;
    }
`;

const customCheckboxStyle = css`
    && {
        margin: 0;
    }
    && label {
        margin: 0;
        padding-left: 20px;
        min-height: 20px;
        cursor: pointer;
    }
    && .check-label {
        display: none;
    }
`;

const ClearFiltersButton = styled.button`
    display: inline-block;
    height: 32px;
    padding: 0 24px;
    border-radius: 40px;
    font-family: Muli;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    box-sizing: border-box;
    white-space: nowrap;
    transition: all 0.2s;
    margin: 0;
    
    background-color: transparent;
    color: #4a5568;
    border: 1px solid #e2e8f0;

    &:hover {
        background-color: #f7fafc;
        color: #2d3748;
        border-color: #cbd5e0;
    }
    
    &:active {
        background-color: #edf2f7;
    }`;

const BulkUpdatePanel = styled.div`
    background-color: #f7fafc;
    border: 1px solid ${silver_two};
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 24px;
    box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.02);
`;

const BulkUpdateTitle = styled.div`
    font-family: Muli;
    font-size: 14px !important;
    font-weight: 700;
    color: #2b6cb0;
    margin: 0 0 16px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    display: flex;
    align-items: center;
    border-left: 3px solid #2b6cb0;
    padding-left: 10px;
`;

const BulkUpdateGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(290px, 1fr));
    gap: 16px;
    @media (max-width: 576px) {
        grid-template-columns: 1fr;
    }
`;


const DateTagInputContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    border: 1px solid ${silver_two};
    border-radius: 4px;
    padding: 4px 8px;
    background: white;
    min-height: 36px;
    flex: 1;

    &:focus-within {
        border-color: ${color_colors_light_blue};
        box-shadow: 0 0 0 1px ${color_colors_light_blue};
    }
`;

const DatePill = styled.div`
    display: inline-flex;
    align-items: center;
    background: #eef6ff;
    color: #1e3a8a;
    font-size: 13px;
    padding: 2px 6px;
    border-radius: 4px;
    gap: 4px;

    button {
        background: transparent;
        border: none;
        color: #1e3a8a;
        cursor: pointer;
        font-size: 14px;
        line-height: 1;
        padding: 0;
        display: flex;
        align-items: center;
        justify-content: center;

        &:hover {
            color: #1e40af;
        }
    }
`;

const DateInput = styled.input`
    border: none;
    outline: none;
    flex: 1;
    min-width: 100px;
    font-family: Muli;
    font-size: 13px;
    background: transparent;
    color: inherit;
    box-sizing: border-box;

    &::placeholder {
        font-weight: normal;
        font-style: italic;
        letter-spacing: normal;
        color: ${color_shades_darker};
    }
`;

const BulkUpdateFieldCard = styled.div`
    background-color: #ffffff;
    border: 1px solid ${silver_two};
    border-radius: 6px;
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 10px;

    .field-label {
        font-family: Muli;
        font-size: 12px;
        font-weight: 700;
        color: #4a5568;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }

    .field-row {
        display: flex;
        align-items: center;
        gap: 8px;

        > *:first-child {
            flex: 1;
            min-width: 0;
        }

        button {
            margin: 0;
            flex-shrink: 0;
        }
    }
`;

const LoaderContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 48px;
    gap: 16px;
    background-color: #ffffff;
    position: relative;
`;

const NoModuleBadge = styled.span`
    font-family: Muli;
    font-size: 13px;
    color: #718096;
    font-style: italic;
`;

const ClosedDayPill = styled.span`
    display: inline-block;
    padding: 2px 6px;
    border-radius: 12px;
    font-size: 9px;
    font-weight: 700;
    background-color: #EBF8FF;
    color: #2B6CB0;
    border: 1px solid #BEE3F8;
    white-space: nowrap;
`;


const ToastWrapper = styled.div`
    position: fixed;
    bottom: 20px;
    right: 0;
    z-index: 9999;
    display: flex;
    flex-direction: column;
    gap: 10px;
    
    > div > div {
        position: relative !important;
        bottom: auto !important;
        right: auto !important;
    }
`;

export default SparkOrganizationManagementSettings;

const ActionRequiredCard = styled.div`
    background-color: #fff5f5;
    border: 1px solid #feb2b2;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 16px;
`;

const ActionRequiredHeader = styled.div`
    display: flex;
    align-items: center;
    color: #c53030;
    font-weight: 700;
    font-size: 14px;
    margin-bottom: 8px;
    
    img {
        margin-left: 0;
        margin-right: 8px;
    }
`;

const ActionRequiredList = styled.ul`
    margin: 0;
    padding-left: 24px;
    color: #2d3748;
    font-size: 13px;
`;

const ActionRequiredItem = styled.li`
    margin-bottom: 4px;
    
    strong {
        font-weight: 600;
    }
`;

const ActionRequiredLink = styled.a`
    color: #2b6cb0;
    text-decoration: none;
    cursor: pointer;
    
    &:hover {
        text-decoration: underline;
    }
`;
